import { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import { drawFabric, drawThreads } from './ThreadRenderer'
import StitchPlayer from './StitchPlayer'

// ─────────────────────────────────────────────────────────────
//  EMBROIDERY EDITOR
//  Interactive stitch editing canvas with floating properties,
//  click/lasso selection, and per-shape editing tools.
// ─────────────────────────────────────────────────────────────

const API_URL = import.meta.env.VITE_API_URL || ''

// ── TOOL DEFINITIONS ──────────────────────────────────────────
const TOOLS = {
  select: { id: 'select', label: 'Select', icon: '⬚', cursor: 'default', tip: 'Click shapes or drag to lasso' },
  pan:    { id: 'pan',    label: 'Pan',    icon: '✋', cursor: 'grab',    tip: 'Drag to pan the canvas' },
  erase:  { id: 'erase',  label: 'Erase',  icon: '✕',  cursor: 'crosshair', tip: 'Click or drag to erase stitches' },
  text:   { id: 'text',   label: 'Text',   icon: 'T',  cursor: 'text',   tip: 'Click to place text' },
  color:  { id: 'color',  label: 'Color',  icon: '●',  cursor: 'pointer', tip: 'Click a shape to change its color' },
  satin:  { id: 'satin',  label: 'Satin',  icon: '∿',  cursor: 'crosshair', tip: 'Click to drop control points along a stem/edge; double-click to finish' },
}

// ── COLOR PALETTE (common embroidery thread colors) ──────────
const THREAD_PALETTE = [
  '#000000', '#FFFFFF', '#FF0000', '#CC0000', '#990000',
  '#FF6600', '#FF9900', '#FFCC00', '#FFFF00', '#CCFF00',
  '#00CC00', '#009900', '#006600', '#00CCCC', '#0099CC',
  '#0066CC', '#0033CC', '#0000FF', '#3300CC', '#6600CC',
  '#9900CC', '#CC00CC', '#FF00CC', '#FF6699', '#CC9966',
  '#996633', '#663300', '#333333', '#666666', '#999999',
]

// ── FONT OPTIONS ─────────────────────────────────────────────
const FONT_OPTIONS = [
  { id: 'block', name: 'Block Bold' },
  { id: 'serif', name: 'Classic Serif' },
  { id: 'sans',  name: 'Clean Sans' },
  { id: 'mono',  name: 'Monospace' },
]

const STITCH_TYPES = [
  { id: 'fill',    name: 'Tatami Fill',    desc: 'Dense rows -- solid coverage' },
  { id: 'satin',   name: 'Satin Column',   desc: 'Perpendicular zigzag -- shiny finish' },
  { id: 'running', name: 'Running Stitch',  desc: 'Single line -- outline only' },
]

// ── HOOP SIZES (mm) — common consumer + commercial machines ──
const HOOP_OPTIONS = [
  { id: '4x4',     name: '4×4"',      w: 100, h: 100, machines: 'PE-770, PE-800, most home' },
  { id: '5x7',     name: '5×7"',      w: 130, h: 180, machines: 'PE-880, Brother SE-2000' },
  { id: '6x10',    name: '6×10"',     w: 160, h: 260, machines: 'Babylock, Janome MC-500E' },
  { id: '8x12',    name: '8×12"',     w: 200, h: 300, machines: 'Brother PE-900, Bernina' },
  { id: '9.5x14',  name: '9.5×14"',   w: 240, h: 360, machines: 'Multi-needle, commercial' },
  { id: 'jumbo',   name: 'Jumbo 14×14"', w: 360, h: 360, machines: 'Tajima, Melco commercial' },
]

// ── BORDER OPTIONS ────────────────────────────────────────────
// Border = a satin column following a perimeter shape. Decoupled into
// SHAPE (rectangle, square, rounded, circle, oval, scallop) and WEIGHT
// (none, light, medium, heavy). The shape determines the perimeter we
// trace; the weight determines the satin-column width.
const BORDER_SHAPES = [
  { id: 'rect',    name: 'Rectangle' },
  { id: 'square',  name: 'Square (1:1)' },
  { id: 'rounded', name: 'Rounded Rectangle' },
  { id: 'circle',  name: 'Circle' },
  { id: 'oval',    name: 'Oval' },
  { id: 'scallop', name: 'Scallop Edge' },
]
const BORDER_OPTIONS = [
  { id: 'none',   name: 'No Border',     width: 0 },
  { id: 'light',  name: 'Light',         width: 10 },   // 1mm satin
  { id: 'medium', name: 'Medium',        width: 20 },   // 2mm satin
  { id: 'heavy',  name: 'Heavy',         width: 35 },   // 3.5mm satin
]

// ── OUTLINE OPTIONS (stitch type + thickness) ──────────────
const OUTLINE_TYPES = [
  { id: 'none', name: 'No Outline' },
  { id: 'running', name: 'Running Stitch', desc: 'Clean single line' },
  { id: 'satin', name: 'Satin Stitch', desc: 'Zigzag back & forth' },
  { id: 'bean', name: 'Bean Stitch', desc: 'Triple-pass thick line' },
]
const OUTLINE_THICKNESS = [
  { id: 'light', name: 'Light' },
  { id: 'medium', name: 'Medium' },
  { id: 'heavy', name: 'Heavy' },
]

// ═════════════════════════════════════════════════════════════
//  FLOATING PANEL (draggable)
// ═════════════════════════════════════════════════════════════
// ── Brush-erase helper ────────────────────────────────────────
// Removes only stitch points within `radiusMm` of the brush center.
// Drops segments that fall below 2 points and shapes that lose all
// segments. Preserves shape metadata (color, type, etc.) for survivors.
// Build a satin-column stitch polyline from a centerline + width. Mirrors
// ai_plan_digitize.satin_column on the backend so manually-drawn columns
// look identical to AI-planned ones. Smooths the centerline (3 passes,
// 3-tap moving average) so the perpendicular normal at each stitch lands
// parallel to its neighbors — clean satin column instead of jittered edge.
function buildSatinColumnPoints (centerlineMm, widthMm, stitchStepMm = 0.4) {
  if (!centerlineMm || centerlineMm.length < 2) return []

  // 3-tap smoothing
  let arr = centerlineMm.map(p => [p[0], p[1]])
  for (let pass = 0; pass < 3; pass++) {
    if (arr.length < 3) break
    const next = [arr[0]]
    for (let i = 1; i < arr.length - 1; i++) {
      next.push([
        (arr[i - 1][0] + arr[i][0] + arr[i + 1][0]) / 3,
        (arr[i - 1][1] + arr[i][1] + arr[i + 1][1]) / 3,
      ])
    }
    next.push(arr[arr.length - 1])
    arr = next
  }

  // Resample at constant arc-length step
  const resampled = [arr[0]]
  let accum = 0, target = stitchStepMm
  for (let i = 1; i < arr.length; i++) {
    const [x0, y0] = arr[i - 1]
    const [x1, y1] = arr[i]
    const seg = Math.hypot(x1 - x0, y1 - y0)
    if (seg < 1e-6) continue
    let consumed = 0
    while (consumed + (target - accum) <= seg) {
      const d = target - accum
      const t = (consumed + d) / seg
      resampled.push([x0 + (x1 - x0) * t, y0 + (y1 - y0) * t])
      consumed += d
      accum = 0
      target = stitchStepMm
    }
    accum += seg - consumed
  }
  if (resampled[resampled.length - 1] !== arr[arr.length - 1]) {
    resampled.push(arr[arr.length - 1])
  }
  if (resampled.length < 2) return []

  // Emit zigzag with 7-point window-smoothed tangent
  const half = widthMm / 2
  const out = []
  for (let i = 0; i < resampled.length; i++) {
    const [x, y] = resampled[i]
    const lo = Math.max(0, i - 3)
    const hi = Math.min(resampled.length - 1, i + 3)
    let tx, ty
    if (hi - lo >= 2) {
      tx = resampled[hi][0] - resampled[lo][0]
      ty = resampled[hi][1] - resampled[lo][1]
    } else {
      const j = Math.max(0, Math.min(resampled.length - 2, i))
      tx = resampled[j + 1][0] - resampled[j][0]
      ty = resampled[j + 1][1] - resampled[j][1]
    }
    const tlen = Math.hypot(tx, ty) || 1
    const nx = -ty / tlen, ny = tx / tlen
    const left = [x + nx * half, y + ny * half]
    const right = [x - nx * half, y - ny * half]
    // i=0 plants both rails; subsequent samples emit ONE rail only,
    // alternating sides so consecutive stitches form a real zigzag
    // (not a travel-along-one-rail).
    if (i === 0) { out.push(left); out.push(right) }
    else         { out.push((i % 2 === 1) ? left : right) }
  }
  return out
}

function eraseInBrushRadius (shapes, brushCenter, radiusMm) {
  const r2 = radiusMm * radiusMm
  const out = []
  for (const shape of shapes) {
    let mutated = false
    const newSegs = []
    for (const seg of shape.segments || []) {
      if (seg.type !== 'stitch' || !seg.points) {
        newSegs.push(seg)
        continue
      }
      const kept = []
      let cur = []
      for (const p of seg.points) {
        const dx = p[0] - brushCenter.x
        const dy = p[1] - brushCenter.y
        if (dx * dx + dy * dy < r2) {
          // erased — break the run; flush what we had
          if (cur.length >= 2) kept.push({ ...seg, points: cur })
          cur = []
          mutated = true
        } else {
          cur.push(p)
        }
      }
      if (cur.length >= 2) kept.push({ ...seg, points: cur })
      if (kept.length === 0) {
        mutated = true
      } else {
        newSegs.push(...kept)
      }
    }
    if (newSegs.length === 0) {
      // Whole shape erased — drop it
      continue
    }
    out.push(mutated ? { ...shape, segments: newSegs } : shape)
  }
  return out
}

function FloatingPanel({ title, children, defaultPos, onClose, isOpen }) {
  const panelRef = useRef(null)
  const [pos, setPos] = useState(defaultPos || { x: 20, y: 60 })
  const [dragging, setDragging] = useState(false)
  const dragOffset = useRef({ x: 0, y: 0 })

  const handleMouseDown = useCallback((e) => {
    if (e.target.closest('.panel-content')) return
    setDragging(true)
    const rect = panelRef.current.getBoundingClientRect()
    dragOffset.current = { x: e.clientX - rect.left, y: e.clientY - rect.top }
    e.preventDefault()
  }, [])

  useEffect(() => {
    if (!dragging) return
    const handleMove = (e) => {
      setPos({
        x: e.clientX - dragOffset.current.x,
        y: e.clientY - dragOffset.current.y
      })
    }
    const handleUp = () => setDragging(false)
    window.addEventListener('mousemove', handleMove)
    window.addEventListener('mouseup', handleUp)
    return () => {
      window.removeEventListener('mousemove', handleMove)
      window.removeEventListener('mouseup', handleUp)
    }
  }, [dragging])

  if (!isOpen) return null

  return (
    <div
      ref={panelRef}
      className="fixed z-50 bg-slate-900/95 backdrop-blur-md rounded-xl shadow-2xl border border-slate-700/50 min-w-[260px] max-w-[320px] select-none"
      style={{ left: pos.x, top: pos.y, cursor: dragging ? 'grabbing' : 'default' }}
    >
      {/* Drag handle / header */}
      <div
        onMouseDown={handleMouseDown}
        className="flex items-center justify-between px-3 py-2 cursor-grab active:cursor-grabbing border-b border-slate-700/50"
      >
        <div className="flex items-center gap-2">
          <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
          <span className="text-xs font-semibold text-slate-300 uppercase tracking-wider">{title}</span>
        </div>
        {onClose && (
          <button onClick={onClose} className="text-slate-500 hover:text-slate-300 transition-colors p-0.5">
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>
      {/* Content */}
      <div className="panel-content p-3 space-y-3 max-h-[60vh] overflow-y-auto text-sm">
        {children}
      </div>
    </div>
  )
}

// ═════════════════════════════════════════════════════════════
//  MAIN EMBROIDERY EDITOR
// ═════════════════════════════════════════════════════════════
export default function EmbroideryEditor({
  previewData,        // { shapes, jumps, trims, colors, bounds, stats }
  pesBase64,          // base64 PES for download
  filename,           // original filename
  imageUrl,           // original design image URL
  onClose,            // close editor → back to canvas
  onExport,           // callback when user exports edited version
  onReDigitize,       // callback to re-digitize a shape with new settings
}) {
  // ── State ────────────────────────────────────────────────
  const canvasRef = useRef(null)
  const containerRef = useRef(null)

  // View transform
  const [zoom, setZoom] = useState(1)
  const [pan, setPan] = useState({ x: 0, y: 0 })
  const [canvasSize, setCanvasSize] = useState({ w: 800, h: 600 })

  // Tool state
  const [activeTool, setActiveTool] = useState('select')
  const [isPanning, setIsPanning] = useState(false)
  const [isErasing, setIsErasing] = useState(false)
  const [eraseBrushMm, setEraseBrushMm] = useState(2.5)  // brush radius in mm
  const [eraseStrokeStarted, setEraseStrokeStarted] = useState(false)  // for history grouping
  const eraseCursorRef = useRef(null)  // last cursor mm position
  const [eraseCursorScreen, setEraseCursorScreen] = useState(null) // {x,y} px for ring
  const panStart = useRef(null)

  // Manual satin column tool — user clicks to drop control points along a
  // stem / edge / outline, then double-clicks to finish. The control
  // points are the SMOOTHED CENTERLINE; we generate the satin column
  // perpendicular zigzag from this + the chosen width. The new column
  // uses the active border color (so user can change colors via the
  // existing palette without an extra tool-specific picker).
  const [satinPoints, setSatinPoints] = useState([])  // mm coords [[x,y], ...]
  const [satinWidthMm, setSatinWidthMm] = useState(1.5)

  // Selection
  const [selectedShapes, setSelectedShapes] = useState(new Set())
  const [lassoPoints, setLassoPoints] = useState(null) // array of {x,y} during lasso drag
  const [isLassoing, setIsLassoing] = useState(false)
  // Drag-to-move (active when select tool is held over a selected shape and
  // the user starts dragging). Stores world-space delta from where the drag
  // started. Translates ALL selected shapes by the delta on each move.
  const [isDraggingShapes, setIsDraggingShapes] = useState(false)
  const dragStartWorld = useRef({ x: 0, y: 0 })
  const dragShapesSnapshot = useRef([]) // shapes at drag start, used to apply delta non-cumulatively
  // Show silhouette overlay (filled regions traced from current shapes) —
  // a verify-only diagnostic so user can compare what's stitched vs what
  // should be filled. Off by default; never affects PES output.
  const [showSilhouette, setShowSilhouette] = useState(false)
  // Currently-selected jump index (into the `jumps` useMemo array) — set
  // when the user clicks within a few px of a jump line in select mode.
  // Pressing Delete with this set merges the two segments adjoining the
  // jump into a single segment, eliminating the needle-up move.
  const [selectedJumpIdx, setSelectedJumpIdx] = useState(null)

  // Shapes (mutable copy of preview data for editing)
  const [shapes, setShapes] = useState([])
  const [trims, setTrims] = useState([])
  const [showJumps, setShowJumps] = useState(false)
  const [showTrims, setShowTrims] = useState(false)
  const [showGrid, setShowGrid] = useState(false)
  // Hoop overlay defaults ON — beginners benefit from constant fit
  // visibility without having to remember to toggle it.
  const [showHoop, setShowHoop] = useState(true)
  // Color sort panel toggle + drag state (which row is being dragged)
  const [showColorSort, setShowColorSort] = useState(false)
  const colorDragSrcRef = useRef(null)

  // Border (shape + weight + color, all independent)
  const [borderWeight, setBorderWeight] = useState('none')
  const [borderShape, setBorderShape] = useState('rect')
  const [showBorderMenu, setShowBorderMenu] = useState(false)
  const [borderColor, setBorderColor] = useState('#000000')

  // Outline (stitch type + thickness + color)
  const [outlineType, setOutlineType] = useState('none')
  const [outlineThickness, setOutlineThickness] = useState('medium')
  const [showOutlineMenu, setShowOutlineMenu] = useState(false)
  const [outlineColor, setOutlineColor] = useState('#000000')

  // Panels
  const [showProperties, setShowProperties] = useState(false)
  const [showTextDialog, setShowTextDialog] = useState(false)

  // Text tool state
  const [textInput, setTextInput] = useState('')
  const [textFont, setTextFont] = useState('block')
  const [textStitchType, setTextStitchType] = useState('satin')
  const [textSize, setTextSize] = useState(15)
  const [textColor, setTextColor] = useState('#000000')
  const [textBaseline, setTextBaseline] = useState('straight')
  const [textBaselineRadius, setTextBaselineRadius] = useState(50)
  const [addingText, setAddingText] = useState(false)
  const [restitching, setRestitching] = useState(false)
  // Font previews loaded from /export/font-previews
  const [fontPreviews, setFontPreviews] = useState([])
  useEffect(() => {
    fetch(`${API_URL}/export/font-previews`)
      .then(r => r.ok ? r.json() : Promise.reject(new Error('font preview fetch failed')))
      .then(d => setFontPreviews(d.fonts || []))
      .catch(err => console.warn('font previews:', err))
  }, [])

  // Undo history
  const [history, setHistory] = useState([])
  const [historyIndex, setHistoryIndex] = useState(-1)

  // Fabric color
  const [fabricColor, setFabricColor] = useState('#f5f0e8')

  // Stitch player — upgraded to pro-tool feature set
  const [isPlaying, setIsPlaying] = useState(false)
  const [playProgress, setPlayProgress] = useState(1.0)
  const [playSpeed, setPlaySpeed] = useState(1)
  const [pauseOnColorChange, setPauseOnColorChange] = useState(false)
  const [autoScroll, setAutoScroll] = useState(false)
  const [reverse, setReverse] = useState(false)
  const animFrameRef = useRef(null)
  const playStartRef = useRef(null)
  // Track which color stop the playhead just crossed (for pause-on-color-change)
  const lastColorIdxRef = useRef(0)

  // Hoop selection (default 4x4 — most common home machine hoop)
  const [selectedHoop, setSelectedHoop] = useState('4x4')
  // Auto-pick the smallest hoop that fits the design when previewData
  // first loads. The user can override afterward; this just stops them
  // from seeing a red "X overflows" warning by default on a design
  // that physically fits in any hoop they own.
  const designAutoHoopRef = useRef(false)

  // Comprehensive pre-stitch validation. Computes everything pro tools
  // surface in their pre-flight panel: counts, time, bobbin, hoop fit,
  // density warnings, long-jump warnings.
  const stats = useMemo(() => {
    if (!previewData?.stats) return null
    const totalStitches = shapes.reduce((sum, s) =>
      sum + s.segments.reduce((ss, seg) => ss + (seg.points?.length || 0), 0), 0)

    // Walk every stitch pair: thread length + long-jump count
    let threadLengthMm = 0
    let longStitchCount = 0
    const LONG_STITCH_MM = 7.0  // anything over 7mm is snag risk
    shapes.forEach(s => s.segments.forEach(seg => {
      if (!seg.points) return
      for (let i = 1; i < seg.points.length; i++) {
        const dx = seg.points[i][0] - seg.points[i-1][0]
        const dy = seg.points[i][1] - seg.points[i-1][1]
        const d = Math.sqrt(dx*dx + dy*dy)
        threadLengthMm += d
        if (d > LONG_STITCH_MM) longStitchCount++
      }
    }))

    const trimCount = previewData.stats.trimCount || previewData.trims?.length || 0
    // Derive jump count from current shapes (mirrors the `jumps` useMemo
    // below — every segment boundary is a real machine jump).
    let jumpCount = 0
    let _lastEnd = null
    for (const shape of shapes) {
      if (shape.isBorder || shape.isOutline) continue
      for (const seg of shape.segments || []) {
        const pts = seg.points || []
        if (pts.length === 0) continue
        if (_lastEnd) {
          const ddx = pts[0][0] - _lastEnd[0]
          const ddy = pts[0][1] - _lastEnd[1]
          if (Math.hypot(ddx, ddy) > 0.05) jumpCount++
        }
        _lastEnd = pts[pts.length - 1]
      }
    }
    const colorChanges = previewData.stats.colorChanges
                       ?? Math.max(0, (previewData.colors?.length || 1) - 1)
    const designWidth  = previewData.bounds ? (previewData.bounds.maxX - previewData.bounds.minX) : 0
    const designHeight = previewData.bounds ? (previewData.bounds.maxY - previewData.bounds.minY) : 0
    const designAreaMm2 = designWidth * designHeight
    const stitchesPerMm2 = designAreaMm2 > 0 ? totalStitches / designAreaMm2 : 0

    // Estimate machine time. 800 stitches/sec is mid-range for home machines.
    // Trims add ~1.5s each (machine pauses + repositions). Color changes
    // add ~10s (operator manually changes thread on home machines).
    const stitchSeconds = totalStitches / 800
    const trimSeconds = trimCount * 1.5
    const colorChangeSeconds = colorChanges * 10
    const totalSeconds = stitchSeconds + trimSeconds + colorChangeSeconds
    const minutes = Math.floor(totalSeconds / 60)
    const seconds = Math.round(totalSeconds % 60)

    // Bobbin meters: bobbin thread roughly matches top thread for tatami
    // but is consumed at ~50% the rate (it locks every stitch but doesn't
    // travel the full path). Industry rule of thumb: bobbin = top × 0.6.
    const bobbinM = (threadLengthMm * 0.6) / 1000

    // Hoop fit
    const hoop = HOOP_OPTIONS.find(h => h.id === selectedHoop) || HOOP_OPTIONS[0]
    const fitsHoop = designWidth <= hoop.w && designHeight <= hoop.h

    // Density warnings — stitches/mm² thresholds (per machine embroidery rules):
    //   <0.5: too sparse (gaps in fill)
    //   0.5-50: normal range
    //   >50: too dense (puckers fabric, breaks needles)
    const densityStatus =
      stitchesPerMm2 < 0.5 ? 'low'
        : stitchesPerMm2 > 50 ? 'high'
        : 'ok'

    return {
      stitchCount: totalStitches,
      shapeCount: shapes.length,
      colorCount: previewData.colors?.length || 0,
      colorChanges,
      trimCount,
      jumpCount,
      longStitchCount,
      threadLengthM: (threadLengthMm / 1000).toFixed(1),
      bobbinM: bobbinM.toFixed(1),
      timeMinutes: minutes,
      timeSeconds: seconds,
      timeFormatted: minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`,
      widthMm: designWidth.toFixed(1),
      heightMm: designHeight.toFixed(1),
      densityValue: stitchesPerMm2.toFixed(1),
      densityStatus,
      hoop,
      fitsHoop,
    }
  }, [shapes, previewData, selectedHoop])

  // Total stitch points for player
  const totalStitchPoints = useMemo(() => {
    return shapes.reduce((sum, s) =>
      sum + s.segments.reduce((ss, seg) => ss + (seg.points?.length || 0), 0), 0)
  }, [shapes])

  // Jumps — recomputed from current shape state every render. SAFETY-CRITICAL:
  // the previewer must show EVERY needle-up move the machine will perform, so
  // the user can audit before sending the file to a machine. Any threshold
  // here would HIDE real machine moves the user needs to see.
  //
  // Rule: every segment boundary is a jump. The digitizer emits separate
  // segments specifically because it issued a TRIM or JUMP command there
  // (see digitize_v5.py flush_segment() — only called on JUMP/TRIM). So if
  // the editor sees a segment break, the machine WILL lift the needle.
  //
  // When the user erases stitches and a segment/shape becomes empty, it's
  // skipped — the jump reroutes from the previous surviving endpoint to the
  // next surviving start. That's accurate: that's what the re-emitted PES
  // will encode after the edit is exported.
  const jumps = useMemo(() => {
    const result = []
    let lastEndpoint = null
    let lastShapeId = null
    let lastSegIdx = -1
    let lastColorIdx = null
    for (const shape of shapes) {
      if (shape.isBorder || shape.isOutline) continue
      // Resolve the shape's effective color index. Cross-color
      // transitions are NOT machine jumps — the machine stops, the
      // operator changes thread manually, and resumes at the new
      // position. There is no needle move thread visible on fabric so
      // we don't draw a red dashed line for it.
      const shapeColorIdx = (shape.colorIndex !== undefined)
        ? shape.colorIndex
        : (shape.color || '#000000').toLowerCase()
      for (let segIdx = 0; segIdx < (shape.segments || []).length; segIdx++) {
        const seg = shape.segments[segIdx]
        const pts = seg.points || []
        if (pts.length === 0) continue
        const start = pts[0]
        const end   = pts[pts.length - 1]
        if (lastEndpoint) {
          const dx = start[0] - lastEndpoint[0]
          const dy = start[1] - lastEndpoint[1]
          const isColorChange = (lastColorIdx !== null && lastColorIdx !== shapeColorIdx)
          if (Math.hypot(dx, dy) > 0.05 && !isColorChange) {
            result.push({
              from: lastEndpoint,
              to: start,
              fromShapeId: lastShapeId,
              fromSegIdx: lastSegIdx,
              toShapeId: shape.id,
              toSegIdx: segIdx,
            })
          }
        }
        lastEndpoint = end
        lastShapeId = shape.id
        lastSegIdx = segIdx
        lastColorIdx = shapeColorIdx
      }
    }
    return result
  }, [shapes])

  // Auto-scroll: when playing with auto-scroll on, pan the canvas so the
  // current needle position stays roughly centered. Cheaper than recomputing
  // pan from scratch every frame — only fires when isPlaying + autoScroll.
  useEffect(() => {
    if (!isPlaying || !autoScroll || !canvasSize.w || !canvasSize.h) return
    const targetStitch = Math.floor(playProgress * totalStitchPoints)
    let cum = 0
    let needle = null
    outer:
    for (const shape of shapes) {
      for (const seg of shape.segments) {
        const pts = seg.points || []
        for (let i = 0; i < pts.length; i++) {
          if (cum + i === targetStitch) { needle = pts[i]; break outer }
        }
        cum += pts.length
      }
    }
    if (!needle) return
    // Compute the pan that puts `needle` at the canvas center
    const centerX = canvasSize.w / 2
    const centerY = canvasSize.h / 2
    setPan({ x: centerX - needle[0] * zoom, y: centerY - needle[1] * zoom })
  }, [playProgress, isPlaying, autoScroll, canvasSize.w, canvasSize.h, zoom, shapes, totalStitchPoints])

  // Pre-compute color-block boundaries (cumulative stitch index where each
  // color starts) so the player can detect crossings for pause-on-color-change.
  const colorBlockStarts = useMemo(() => {
    const starts = []
    let cum = 0
    let lastColorIdx = -1
    shapes.forEach(s => {
      const idx = s.colorIndex !== undefined ? s.colorIndex : -1
      if (idx !== lastColorIdx) {
        starts.push({ start: cum, colorIndex: idx })
        lastColorIdx = idx
      }
      cum += s.segments.reduce((a, sg) => a + (sg.points?.length || 0), 0)
    })
    return starts
  }, [shapes])

  // Player animation loop with pause-on-color-change + reverse support
  useEffect(() => {
    if (!isPlaying) return
    playStartRef.current = performance.now()
    const startProgress = reverse
      ? (playProgress <= 0 ? 1.0 : playProgress)
      : (playProgress >= 1.0 ? 0.0 : playProgress)
    const startStitch = startProgress * totalStitchPoints
    let startBlockIdx = 0
    for (let i = 0; i < colorBlockStarts.length; i++) {
      if (colorBlockStarts[i].start <= startStitch) startBlockIdx = i
    }
    lastColorIdxRef.current = startBlockIdx
    const animate = (now) => {
      const elapsed = (now - playStartRef.current) / 1000
      const stitchesPerSec = 800 * playSpeed * (reverse ? -1 : 1)
      const dProgress = elapsed * stitchesPerSec / Math.max(1, totalStitchPoints)
      const newProgress = Math.max(0, Math.min(1, startProgress + dProgress))
      const newStitch = newProgress * totalStitchPoints
      // Color-block crossing detection (forward direction only)
      if (!reverse) {
        let newBlockIdx = lastColorIdxRef.current
        for (let i = lastColorIdxRef.current + 1; i < colorBlockStarts.length; i++) {
          if (colorBlockStarts[i].start <= newStitch) newBlockIdx = i
        }
        if (pauseOnColorChange && newBlockIdx > lastColorIdxRef.current) {
          setPlayProgress(colorBlockStarts[newBlockIdx].start / totalStitchPoints)
          setIsPlaying(false)
          return
        }
        lastColorIdxRef.current = newBlockIdx
      }
      setPlayProgress(newProgress)
      const atEnd = reverse ? newProgress <= 0 : newProgress >= 1.0
      if (!atEnd) {
        animFrameRef.current = requestAnimationFrame(animate)
      } else {
        setIsPlaying(false)
      }
    }
    animFrameRef.current = requestAnimationFrame(animate)
    return () => { if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current) }
  }, [isPlaying, playSpeed, totalStitchPoints, pauseOnColorChange, colorBlockStarts, reverse])

  // ── Initialize from preview data ────────────────────────
  useEffect(() => {
    if (!previewData) return
    const shapesWithId = previewData.shapes.map((s, i) => ({
      ...s,
      id: `shape-${i}`,
      name: `Shape ${i + 1}`,
      stitchType: 'fill', // default; we can detect from stitch pattern later
      outlineWidth: 0,
      density: 1.0,
    }))
    setShapes(shapesWithId)
    // NOTE: jumps are NOT stored — they're derived from current shapes via
    // the `jumps` useMemo below so that erasing stitches automatically
    // reroutes around the gap instead of leaving stale red dashed lines
    // pointing into deleted regions.
    setTrims(previewData.trims || [])
    if (typeof console !== 'undefined') {
      console.log(`[EmbroideryEditor] previewData → ${shapesWithId.length} shapes, ${(previewData.trims || []).length} trims`)
    }
    // Save initial state to history
    setHistory([shapesWithId])
    setHistoryIndex(0)
    // Auto-pick the smallest hoop that fits the design. Skip if the
    // user has already manually changed the hoop on this preview load
    // (designAutoHoopRef gets latched after one auto-pick).
    if (!designAutoHoopRef.current && previewData?.bounds) {
      const dw = previewData.bounds.maxX - previewData.bounds.minX
      const dh = previewData.bounds.maxY - previewData.bounds.minY
      // Order HOOP_OPTIONS smallest → largest; pick first that fits.
      const sorted = [...HOOP_OPTIONS].sort((a, b) => (a.w * a.h) - (b.w * b.h))
      const fit = sorted.find(h => dw <= h.w && dh <= h.h)
      if (fit) {
        setSelectedHoop(fit.id)
        designAutoHoopRef.current = true
      }
    }
  }, [previewData])

  // ── Fit view on mount ───────────────────────────────────
  useEffect(() => {
    if (!previewData?.bounds || !containerRef.current) return
    const { minX, minY, maxX, maxY } = previewData.bounds
    const dw = maxX - minX
    const dh = maxY - minY
    if (dw < 1 || dh < 1) return

    const rect = containerRef.current.getBoundingClientRect()
    const cw = rect.width || 800
    const ch = rect.height || 600
    setCanvasSize({ w: cw, h: ch })

    const pad = 60
    const scaleX = (cw - pad * 2) / dw
    const scaleY = (ch - pad * 2) / dh
    const fitZoom = Math.min(scaleX, scaleY, 8)

    setZoom(fitZoom)
    setPan({
      x: (cw / 2) - ((minX + dw / 2) * fitZoom),
      y: (ch / 2) - ((minY + dh / 2) * fitZoom)
    })
  }, [previewData])

  // ── Apply border changes ─────────────────────────────────
  useEffect(() => {
    setShapes(prev => {
      const withoutBorder = prev.filter(s => !s.isBorder)
      if (borderWeight === 'none') return withoutBorder
      const border = generateBorderShape(borderWeight, borderColor, borderShape)
      if (!border) return withoutBorder
      return [...withoutBorder, border]
    })
  }, [borderWeight, borderColor, borderShape, previewData])

  // ── Apply outline changes ───────────────────────────────
  useEffect(() => {
    setShapes(prev => {
      const withoutOutlines = prev.filter(s => !s.isOutline)
      if (outlineType === 'none') return withoutOutlines
      const outlines = generateOutlineShapes(outlineType, outlineThickness, outlineColor)
      return [...withoutOutlines, ...outlines]
    })
  }, [outlineType, outlineThickness, outlineColor, previewData])

  // ── Canvas rendering ────────────────────────────────────
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas || shapes.length === 0) return
    const ctx = canvas.getContext('2d')
    const { w, h } = canvasSize

    canvas.width = w * window.devicePixelRatio
    canvas.height = h * window.devicePixelRatio
    canvas.style.width = `${w}px`
    canvas.style.height = `${h}px`
    ctx.setTransform(window.devicePixelRatio, 0, 0, window.devicePixelRatio, 0, 0)

    // Clear with user-selected fabric color (solid base)
    ctx.fillStyle = fabricColor
    ctx.fillRect(0, 0, w, h)

    // Overlay tileable fabric weave texture in design space — gives the
    // backdrop a real cotton/linen feel that thread sprites cast shadow on.
    if (previewData?.bounds) {
      ctx.save()
      ctx.translate(pan.x, pan.y)
      ctx.scale(zoom, zoom)
      drawFabric(ctx, previewData.bounds, fabricColor)
      ctx.restore()
    }

    // Grid
    if (showGrid) {
      ctx.save()
      ctx.translate(pan.x, pan.y)
      ctx.scale(zoom, zoom)
      const gridStep = 10 // 10 EU = 1mm
      const b = previewData.bounds
      ctx.strokeStyle = 'rgba(0,0,0,0.06)'
      ctx.lineWidth = 0.5 / zoom
      for (let x = Math.floor(b.minX / gridStep) * gridStep; x < b.maxX; x += gridStep) {
        ctx.beginPath()
        ctx.moveTo(x, b.minY - 20)
        ctx.lineTo(x, b.maxY + 20)
        ctx.stroke()
      }
      for (let y = Math.floor(b.minY / gridStep) * gridStep; y < b.maxY; y += gridStep) {
        ctx.beginPath()
        ctx.moveTo(b.minX - 20, y)
        ctx.lineTo(b.maxX + 20, y)
        ctx.stroke()
      }
      ctx.restore()
    }

    // ═══ HOOP OVERLAY ═══
    // Translucent rectangle showing the hoop boundary at the design's
    // center. Helps the user spot fit issues at any zoom — turns red
    // when the design overflows. Drawn before the shapes so stitches
    // sit on top.
    if (showHoop && previewData.bounds) {
      const hoop = HOOP_OPTIONS.find(h => h.id === selectedHoop) || HOOP_OPTIONS[0]
      const b = previewData.bounds
      const cx = (b.minX + b.maxX) / 2, cy = (b.minY + b.maxY) / 2
      const hx = cx - hoop.w / 2, hy = cy - hoop.h / 2
      const fits = (b.maxX - b.minX) <= hoop.w && (b.maxY - b.minY) <= hoop.h
      ctx.save()
      ctx.translate(pan.x, pan.y)
      ctx.scale(zoom, zoom)
      // Soft fill so the hoop area is visible without obscuring stitches
      ctx.fillStyle = fits ? 'rgba(34, 197, 94, 0.05)' : 'rgba(239, 68, 68, 0.07)'
      ctx.fillRect(hx, hy, hoop.w, hoop.h)
      ctx.strokeStyle = fits ? 'rgba(34, 197, 94, 0.7)' : 'rgba(239, 68, 68, 0.85)'
      ctx.lineWidth = 1.0 / zoom
      ctx.setLineDash([4 / zoom, 3 / zoom])
      ctx.strokeRect(hx, hy, hoop.w, hoop.h)
      ctx.setLineDash([])
      // Hoop label in screen-space so it stays legible at any zoom
      ctx.restore()
      ctx.save()
      ctx.setTransform(1, 0, 0, 1, 0, 0)
      const sx = hx * zoom + pan.x
      const sy = hy * zoom + pan.y
      ctx.fillStyle = fits ? '#16a34a' : '#dc2626'
      ctx.font = '11px ui-sans-serif, system-ui'
      ctx.fillText(`${hoop.name} hoop ${fits ? '✓ fits' : '✗ overflows'}`,
                   Math.max(8, sx + 4), Math.max(14, sy - 4))
      ctx.restore()
    }

    // Draw shapes — texture-fill rendering for professional thread appearance
    ctx.save()
    ctx.translate(pan.x, pan.y)
    ctx.scale(zoom, zoom)

    const maxStitchesToDraw = Math.floor(playProgress * totalStitchPoints)
    let stitchesDrawn = 0
    let needlePos = null

    // ═══ SILHOUETTE OVERLAY (verify-only, opt-in) ═══
    // Computes a translucent fill from each shape's stitch convex hull and
    // draws it BENEATH the actual stitch lines so the user can compare:
    // does the machine output cover the intended silhouette? Areas where
    // silhouette shows through with no stitches on top = gap. Areas where
    // stitches extend beyond silhouette = bleed.
    // Off by default. Never written to PES.
    if (showSilhouette) {
      ctx.save()
      ctx.globalAlpha = 0.18
      shapes.forEach((shape) => {
        if (shape.isBorder || shape.isOutline) return
        // Aggregate all stitch points for this shape into one polygon hull.
        const allPts = []
        shape.segments.forEach(seg => {
          if (seg.type !== 'stitch' || !seg.points) return
          seg.points.forEach(p => allPts.push(p))
        })
        if (allPts.length < 4) return
        // Quick convex hull (Graham scan) — good-enough silhouette for
        // verification. Not a true alpha-shape but cheap and obvious.
        const hull = _convexHull(allPts)
        if (hull.length < 3) return
        let color = shape.color || '#000000'
        if (previewData?.colors?.length > 0 && shape.colorIndex !== undefined) {
          const c = previewData.colors[shape.colorIndex]
          if (c) color = typeof c === 'string' ? c : (c.hex || `rgb(${c.r},${c.g},${c.b})`)
        }
        ctx.fillStyle = color
        ctx.beginPath()
        ctx.moveTo(hull[0][0], hull[0][1])
        for (let i = 1; i < hull.length; i++) ctx.lineTo(hull[i][0], hull[i][1])
        ctx.closePath()
        ctx.fill()
      })
      ctx.restore()
    }

    // ═══ STITCH RENDERING ═══
    // Render the EXACT stitch path the machine will sew. Every line drawn here
    // is a real stitch from the PES file — no synthetic fill, no fake texture.
    // Satin renders as visible zigzag along the line strokes; tatami fill
    // renders as the actual scan lines that will sit on the fabric. This is
    // what a real embroidery preview shows — what you see is what stitches.
    shapes.forEach((shape) => {
      if (stitchesDrawn >= maxStitchesToDraw) return
      const isSelected = selectedShapes.has(shape.id)
      let color = shape.color || '#000000'
      if (previewData?.colors?.length > 0 && shape.colorIndex !== undefined) {
        const c = previewData.colors[shape.colorIndex]
        if (c) color = typeof c === 'string' ? c : (c.hex || `rgb(${c.r},${c.g},${c.b})`)
      }

      const stitchSegs = []
      shape.segments.forEach(seg => {
        if (seg.type !== 'stitch' || !seg.points || seg.points.length < 2) return
        if (stitchesDrawn >= maxStitchesToDraw) return
        stitchesDrawn++
        for (let i = 1; i < seg.points.length; i++) {
          if (stitchesDrawn >= maxStitchesToDraw) break
          stitchSegs.push([seg.points[i-1][0], seg.points[i-1][1], seg.points[i][0], seg.points[i][1]])
          needlePos = { x: seg.points[i][0], y: seg.points[i][1] }
          stitchesDrawn++
        }
      })

      // ═══ 3D thread sprites ═══════════════════════════════
      // Pre-rendered thread sprites per color (cross-section gradient +
      // sheen baked in), blitted with rotation at each stitch position.
      // Two passes: drop-shadow first, color thread on top. This is the
      // ONLY visual layer — what you see is exactly what stitches.
      // Real 40wt embroidery thread is ~0.36mm wide. Render at that width
      // so the preview matches what the machine produces — at 0.15mm row
      // spacing the threads overlap visually and tatami fills look solid,
      // not striped. Thinner render widths were lying about visible gaps
      // that won't actually exist on fabric.
      // Thread render thickness — bumped from 0.36 (real 40wt physical)
      // to 0.5 to match how thread reads on actual fabric: real thread
      // sags and fans out covering ~30% more than its raw diameter.
      // Without the bump, adjacent tatami rows show visible gaps in
      // the editor even at correct row spacing.
      const threadW = isSelected ? 0.55 : 0.5
      drawThreads(ctx, stitchSegs, color, threadW, zoom)
    })

    // Needle crosshair — only when player is mid-design (not at end), so the
    // user can SEE where stitching is currently happening during playback.
    if (needlePos && playProgress < 1.0 - 1e-6 && playProgress > 0) {
      ctx.save()
      ctx.setTransform(1, 0, 0, 1, 0, 0)
      const sx = needlePos.x * zoom + pan.x
      const sy = needlePos.y * zoom + pan.y
      // Outer ring + crosshair
      ctx.strokeStyle = '#ef4444'
      ctx.lineWidth = 1.5
      ctx.beginPath()
      ctx.arc(sx, sy, 8, 0, Math.PI * 2)
      ctx.stroke()
      ctx.beginPath()
      ctx.moveTo(sx - 12, sy); ctx.lineTo(sx - 4, sy)
      ctx.moveTo(sx + 4, sy);  ctx.lineTo(sx + 12, sy)
      ctx.moveTo(sx, sy - 12); ctx.lineTo(sx, sy - 4)
      ctx.moveTo(sx, sy + 4);  ctx.lineTo(sx, sy + 12)
      ctx.stroke()
      // Center dot
      ctx.fillStyle = '#ef4444'
      ctx.beginPath()
      ctx.arc(sx, sy, 1.5, 0, Math.PI * 2)
      ctx.fill()
      ctx.restore()
    }

    // Draw jump stitches — render in SCREEN-SPACE pixels (independent of mm/zoom)
    // so they're always visible at any zoom level. We temporarily reset the
    // transform, project the mm coordinates into screen space, then draw.
    if (showJumps && jumps.length > 0) {
      ctx.save()
      ctx.setTransform(1, 0, 0, 1, 0, 0)            // back to screen-pixel space
      ctx.setLineDash([6, 4])                        // 6px dash, 4px gap — always visible
      ctx.lineCap = 'butt'
      jumps.forEach((j, ji) => {
        const fx = j.from[0] * zoom + pan.x
        const fy = j.from[1] * zoom + pan.y
        const tx = j.to[0]   * zoom + pan.x
        const ty = j.to[1]   * zoom + pan.y
        // Selected jump: draw thick green to make it obvious + add a
        // hint that Delete will merge.
        if (ji === selectedJumpIdx) {
          ctx.strokeStyle = '#10b981'
          ctx.lineWidth = 3
        } else {
          ctx.strokeStyle = '#ef4444'
          ctx.lineWidth = 1.5
        }
        ctx.beginPath()
        ctx.moveTo(fx, fy)
        ctx.lineTo(tx, ty)
        ctx.stroke()
      })
      ctx.setLineDash([])
      // Red dot at each jump endpoint — needle drop indicator
      ctx.fillStyle = '#ef4444'
      jumps.forEach(j => {
        const tx = j.to[0] * zoom + pan.x
        const ty = j.to[1] * zoom + pan.y
        ctx.beginPath()
        ctx.arc(tx, ty, 2.5, 0, Math.PI * 2)
        ctx.fill()
      })
      ctx.restore()
    }

    // Draw trim markers — also in screen space so they don't get crushed by zoom.
    // Sample down when there are too many to keep the canvas readable.
    if (showTrims && trims.length > 0) {
      ctx.save()
      ctx.setTransform(1, 0, 0, 1, 0, 0)
      ctx.fillStyle = '#f59e0b'
      const trimSize = 4
      const stride = trims.length > 2000 ? Math.ceil(trims.length / 800) : 1
      for (let i = 0; i < trims.length; i += stride) {
        const t = trims[i]
        const cx = t.position[0] * zoom + pan.x
        const cy = t.position[1] * zoom + pan.y
        ctx.beginPath()
        ctx.moveTo(cx, cy - trimSize)
        ctx.lineTo(cx + trimSize, cy + trimSize)
        ctx.lineTo(cx - trimSize, cy + trimSize)
        ctx.closePath()
        ctx.fill()
      }
      ctx.restore()
    }

    ctx.restore()

    // Draw lasso
    if (lassoPoints && lassoPoints.length > 1) {
      ctx.strokeStyle = '#3b82f6'
      ctx.lineWidth = 1.5
      ctx.setLineDash([5, 5])
      ctx.beginPath()
      ctx.moveTo(lassoPoints[0].x, lassoPoints[0].y)
      lassoPoints.forEach(p => ctx.lineTo(p.x, p.y))
      ctx.closePath()
      ctx.stroke()
      ctx.fillStyle = 'rgba(59, 130, 246, 0.08)'
      ctx.fill()
      ctx.setLineDash([])
    }

    // Draw in-progress satin column control points + the centerline
    // they form. Lets the user see what they're sketching before
    // double-clicking to commit.
    if (activeTool === 'satin' && satinPoints.length > 0) {
      ctx.save()
      ctx.setTransform(1, 0, 0, 1, 0, 0)
      const screenPts = satinPoints.map(([x, y]) => [x * zoom + pan.x, y * zoom + pan.y])
      // Centerline preview
      if (screenPts.length >= 2) {
        ctx.strokeStyle = '#3b82f6'
        ctx.lineWidth = 2
        ctx.setLineDash([4, 4])
        ctx.beginPath()
        ctx.moveTo(screenPts[0][0], screenPts[0][1])
        for (let i = 1; i < screenPts.length; i++) {
          ctx.lineTo(screenPts[i][0], screenPts[i][1])
        }
        ctx.stroke()
        ctx.setLineDash([])
      }
      // Width preview rectangles around each segment
      if (screenPts.length >= 2) {
        ctx.fillStyle = 'rgba(59, 130, 246, 0.18)'
        const halfPx = (satinWidthMm / 2) * zoom
        for (let i = 0; i < screenPts.length - 1; i++) {
          const [x0, y0] = screenPts[i]
          const [x1, y1] = screenPts[i + 1]
          const tx = x1 - x0, ty = y1 - y0
          const tlen = Math.hypot(tx, ty) || 1
          const nx = -ty / tlen * halfPx
          const ny = tx / tlen * halfPx
          ctx.beginPath()
          ctx.moveTo(x0 + nx, y0 + ny)
          ctx.lineTo(x1 + nx, y1 + ny)
          ctx.lineTo(x1 - nx, y1 - ny)
          ctx.lineTo(x0 - nx, y0 - ny)
          ctx.closePath()
          ctx.fill()
        }
      }
      // Control points
      ctx.fillStyle = '#3b82f6'
      for (const [sx, sy] of screenPts) {
        ctx.beginPath()
        ctx.arc(sx, sy, 4, 0, Math.PI * 2)
        ctx.fill()
      }
      ctx.restore()
    }

  }, [shapes, zoom, pan, canvasSize, selectedShapes, selectedJumpIdx, showJumps, showTrims, showGrid, showSilhouette, showHoop, selectedHoop, jumps, trims, lassoPoints, previewData, fabricColor, playProgress, totalStitchPoints, activeTool, satinPoints, satinWidthMm])

  // ── Helpers ─────────────────────────────────────────────
  function getShapeBbox(shape) {
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
    shape.segments.forEach(seg => {
      if (!seg.points) return
      seg.points.forEach(([x, y]) => {
        if (x < minX) minX = x; if (y < minY) minY = y
        if (x > maxX) maxX = x; if (y > maxY) maxY = y
      })
    })
    if (minX === Infinity) return null
    return { x: minX, y: minY, w: maxX - minX, h: maxY - minY }
  }

  // Generate satin stitch border around the design
  function generateBorderShape(weight, color, shape) {
    const opt = BORDER_OPTIONS.find(b => b.id === weight)
    if (!opt || opt.width === 0 || !previewData?.bounds) return null

    const { minX, minY, maxX, maxY } = previewData.bounds
    const pad = 1.5  // padding (mm) between design edge and inner border edge
    const w = opt.width / 10  // satin column width in mm (option stores EU)
    const spacing = 0.4

    // Build a list of (innerPoint, outerPoint) pairs that walk the chosen
    // perimeter shape. Each pair will become one zigzag stitch from inner
    // to outer (or outer to inner). The shape just defines geometry of
    // those rails; the satin emission below is shape-agnostic.
    const ix1 = minX - pad, iy1 = minY - pad
    const ix2 = maxX + pad, iy2 = maxY + pad
    const cx = (ix1 + ix2) / 2
    const cy = (iy1 + iy2) / 2
    const halfW = (ix2 - ix1) / 2
    const halfH = (iy2 - iy1) / 2

    let pairs = []  // [[innerXY, outerXY], ...]

    const sampleAlongPerimeter = (perimeterFn, totalLen) => {
      const out = []
      const n = Math.max(40, Math.floor(totalLen / spacing))
      for (let i = 0; i <= n; i++) {
        const t = i / n
        const { inner, outer } = perimeterFn(t)
        out.push([inner, outer])
      }
      return out
    }

    if (shape === 'square') {
      // Force 1:1 aspect — pad outward on the smaller axis so the border
      // sits as a clean square around the design (not a stretched square).
      const half = Math.max(halfW, halfH)
      const sx1 = cx - half, sy1 = cy - half
      const sx2 = cx + half, sy2 = cy + half
      const oSx1 = sx1 - w, oSy1 = sy1 - w
      const oSx2 = sx2 + w, oSy2 = sy2 + w
      const sides = [
        { inner: (t) => [sx1 + t * (sx2 - sx1), sy1],
          outer: (t) => [oSx1 + t * (oSx2 - oSx1), oSy1], len: sx2 - sx1 },
        { inner: (t) => [sx2, sy1 + t * (sy2 - sy1)],
          outer: (t) => [oSx2, oSy1 + t * (oSy2 - oSy1)], len: sy2 - sy1 },
        { inner: (t) => [sx2 - t * (sx2 - sx1), sy2],
          outer: (t) => [oSx2 - t * (oSx2 - oSx1), oSy2], len: sx2 - sx1 },
        { inner: (t) => [sx1, sy2 - t * (sy2 - sy1)],
          outer: (t) => [oSx1, oSy2 - t * (oSy2 - oSy1)], len: sy2 - sy1 },
      ]
      for (const side of sides) {
        const steps = Math.max(4, Math.floor(side.len / spacing))
        for (let i = 0; i <= steps; i++) {
          const t = i / steps
          pairs.push([side.inner(t), side.outer(t)])
        }
      }
    } else if (shape === 'rounded') {
      // Rectangle with rounded corners. Corner radius = min(width, height) / 6.
      const r = Math.min(ix2 - ix1, iy2 - iy1) / 6
      const ox1 = ix1 - w, oy1 = iy1 - w
      const ox2 = ix2 + w, oy2 = iy2 + w
      const oR = r + w * 0.0  // outer corner radius matches inner
      // Trace perimeter: top edge (left of TL arc → right of TR arc),
      // TR arc, right edge, BR arc, bottom edge, BL arc, left edge, TL arc.
      const parts = []
      // Top edge
      parts.push({ kind: 'line',
        innerA: [ix1 + r, iy1], innerB: [ix2 - r, iy1],
        outerA: [ix1 + r, oy1], outerB: [ix2 - r, oy1] })
      // TR corner
      parts.push({ kind: 'arc', cx: ix2 - r, cy: iy1 + r,
        ir: r, or: r + w, fromAngle: -Math.PI / 2, toAngle: 0 })
      // Right edge
      parts.push({ kind: 'line',
        innerA: [ix2, iy1 + r], innerB: [ix2, iy2 - r],
        outerA: [ox2, iy1 + r], outerB: [ox2, iy2 - r] })
      // BR corner
      parts.push({ kind: 'arc', cx: ix2 - r, cy: iy2 - r,
        ir: r, or: r + w, fromAngle: 0, toAngle: Math.PI / 2 })
      // Bottom edge
      parts.push({ kind: 'line',
        innerA: [ix2 - r, iy2], innerB: [ix1 + r, iy2],
        outerA: [ix2 - r, oy2], outerB: [ix1 + r, oy2] })
      // BL corner
      parts.push({ kind: 'arc', cx: ix1 + r, cy: iy2 - r,
        ir: r, or: r + w, fromAngle: Math.PI / 2, toAngle: Math.PI })
      // Left edge
      parts.push({ kind: 'line',
        innerA: [ix1, iy2 - r], innerB: [ix1, iy1 + r],
        outerA: [ox1, iy2 - r], outerB: [ox1, iy1 + r] })
      // TL corner
      parts.push({ kind: 'arc', cx: ix1 + r, cy: iy1 + r,
        ir: r, or: r + w, fromAngle: Math.PI, toAngle: 3 * Math.PI / 2 })
      for (const part of parts) {
        if (part.kind === 'line') {
          const len = Math.hypot(part.innerB[0] - part.innerA[0], part.innerB[1] - part.innerA[1])
          const steps = Math.max(2, Math.floor(len / spacing))
          for (let i = 0; i <= steps; i++) {
            const t = i / steps
            const ip = [part.innerA[0] + t * (part.innerB[0] - part.innerA[0]),
                        part.innerA[1] + t * (part.innerB[1] - part.innerA[1])]
            const op = [part.outerA[0] + t * (part.outerB[0] - part.outerA[0]),
                        part.outerA[1] + t * (part.outerB[1] - part.outerA[1])]
            pairs.push([ip, op])
          }
        } else {
          const arcLen = Math.abs(part.toAngle - part.fromAngle) * part.ir
          const steps = Math.max(8, Math.floor(arcLen / spacing))
          for (let i = 0; i <= steps; i++) {
            const t = i / steps
            const a = part.fromAngle + t * (part.toAngle - part.fromAngle)
            pairs.push([
              [part.cx + Math.cos(a) * part.ir, part.cy + Math.sin(a) * part.ir],
              [part.cx + Math.cos(a) * part.or, part.cy + Math.sin(a) * part.or],
            ])
          }
        }
      }
    } else if (shape === 'circle') {
      // Use the larger half-axis as radius — circle that fully wraps the
      // design with the requested padding.
      const r = Math.max(halfW, halfH)
      const circumference = 2 * Math.PI * r
      pairs = sampleAlongPerimeter((t) => {
        const a = t * Math.PI * 2 - Math.PI / 2
        return {
          inner: [cx + Math.cos(a) * r, cy + Math.sin(a) * r],
          outer: [cx + Math.cos(a) * (r + w), cy + Math.sin(a) * (r + w)],
        }
      }, circumference)
    } else if (shape === 'oval') {
      // Ellipse fitting the design's aspect ratio (with pad).
      const a = halfW
      const b = halfH
      // Ramanujan's approximation for ellipse perimeter.
      const h = ((a - b) * (a - b)) / ((a + b) * (a + b))
      const circumference = Math.PI * (a + b) * (1 + 3 * h / (10 + Math.sqrt(4 - 3 * h)))
      pairs = sampleAlongPerimeter((t) => {
        const ang = t * Math.PI * 2 - Math.PI / 2
        // Outer point along the same radial direction
        return {
          inner: [cx + Math.cos(ang) * a, cy + Math.sin(ang) * b],
          outer: [cx + Math.cos(ang) * (a + w), cy + Math.sin(ang) * (b + w)],
        }
      }, circumference)
    } else if (shape === 'scallop') {
      // Rectangle with scalloped (semicircular) bumps along each edge.
      // Pretty for floral / vintage looks.
      const lobeRadius = 4 // mm — diameter of each scallop bump
      const ox1 = ix1 - w, oy1 = iy1 - w
      const ox2 = ix2 + w, oy2 = iy2 + w
      const sides = [
        { dir: 'h', y: iy1, oy: oy1, x0: ix1, x1: ix2, sign: -1 }, // top
        { dir: 'v', x: ix2, ox: ox2, y0: iy1, y1: iy2, sign:  1 }, // right
        { dir: 'h', y: iy2, oy: oy2, x0: ix2, x1: ix1, sign:  1 }, // bottom
        { dir: 'v', x: ix1, ox: ox1, y0: iy2, y1: iy1, sign: -1 }, // left
      ]
      for (const side of sides) {
        const sideLen = Math.abs(side.dir === 'h' ? (side.x1 - side.x0) : (side.y1 - side.y0))
        const lobes = Math.max(3, Math.round(sideLen / (lobeRadius * 2)))
        for (let lobe = 0; lobe < lobes; lobe++) {
          // Each lobe is a semicircle from side.fromT to side.toT.
          const tA = lobe / lobes
          const tB = (lobe + 1) / lobes
          const steps = Math.max(8, Math.floor(lobeRadius * 2 / spacing))
          for (let i = 0; i <= steps; i++) {
            const u = i / steps
            const tt = tA + u * (tB - tA)
            // Base position on the side
            let bx, by, nx, ny
            if (side.dir === 'h') {
              bx = side.x0 + tt * (side.x1 - side.x0)
              by = side.y
              nx = 0; ny = side.sign
            } else {
              bx = side.x
              by = side.y0 + tt * (side.y1 - side.y0)
              nx = side.sign; ny = 0
            }
            // Lobe bump: semicircle bulging outward by lobeRadius along the
            // side's tangent — we offset along normal by lobeRadius * sin(πu)
            const bump = lobeRadius * Math.sin(Math.PI * u)
            const ipx = bx + nx * bump
            const ipy = by + ny * bump
            const opx = bx + nx * (bump + w)
            const opy = by + ny * (bump + w)
            pairs.push([[ipx, ipy], [opx, opy]])
          }
        }
      }
    } else {
      // Default: rectangle. Same as before (4 straight sides).
      const ix1r = ix1, iy1r = iy1, ix2r = ix2, iy2r = iy2
      const ox1r = ix1 - w, oy1r = iy1 - w
      const ox2r = ix2 + w, oy2r = iy2 + w
      const sides = [
        { inner: (t) => [ix1r + t * (ix2r - ix1r), iy1r],
          outer: (t) => [ox1r + t * (ox2r - ox1r), oy1r], len: ix2r - ix1r },
        { inner: (t) => [ix2r, iy1r + t * (iy2r - iy1r)],
          outer: (t) => [ox2r, oy1r + t * (oy2r - oy1r)], len: iy2r - iy1r },
        { inner: (t) => [ix2r - t * (ix2r - ix1r), iy2r],
          outer: (t) => [ox2r - t * (ox2r - ox1r), oy2r], len: ix2r - ix1r },
        { inner: (t) => [ix1r, iy2r - t * (iy2r - iy1r)],
          outer: (t) => [ox1r - t * (ox2r - ox1r), oy2r - t * (oy2r - oy1r)], len: iy2r - iy1r },
      ]
      for (const side of sides) {
        const steps = Math.max(4, Math.floor(side.len / spacing))
        for (let i = 0; i <= steps; i++) {
          const t = i / steps
          pairs.push([side.inner(t), side.outer(t)])
        }
      }
    }

    // Convert pair list into a satin-column point sequence: alternate
    // (inner, outer) and (outer, inner) per pair so the connecting line
    // between consecutive pair-emits stays SHORT (along the perimeter,
    // not diagonally across the column). The previous code emitted
    // (inner, outer) every time, which produced spikes between pairs.
    const points = []
    let goingOut = true
    for (const [inner, outer] of pairs) {
      if (goingOut) { points.push(inner); points.push(outer) }
      else          { points.push(outer); points.push(inner) }
      goingOut = !goingOut
    }

    return {
      id: 'border-shape',
      name: 'Border',
      color: color,
      stitchType: 'satin',
      outlineWidth: 0,
      density: 1.0,
      isBorder: true,
      segments: [{ type: 'stitch', points }]
    }
  }

  // Outline: render each shape in a unique color on offscreen canvas,
  // then trace edges where colors differ. This captures BOTH the outer
  // silhouette AND internal boundaries between shapes (like V18 did).
  function generateOutlineShapes(stitchType, thickness, color) {
    if (!previewData?.bounds) return []

    const { minX, minY, maxX, maxY } = previewData.bounds
    const designW = maxX - minX, designH = maxY - minY
    if (designW < 1 || designH < 1) return []

    // Render at ~1px per world unit, capped for performance
    const maxDim = 600
    const scale = Math.min(1, maxDim / Math.max(designW, designH))
    const pad = 4
    const cw = Math.ceil(designW * scale) + pad * 2
    const ch = Math.ceil(designH * scale) + pad * 2
    if (cw * ch > 1000000) return []

    const offscreen = document.createElement('canvas')
    offscreen.width = cw; offscreen.height = ch
    const ctx = offscreen.getContext('2d')
    ctx.clearRect(0, 0, cw, ch)

    // Draw each shape in a UNIQUE color so we can detect inter-shape boundaries
    const realShapes = shapes.filter(s => !s.isBorder && !s.isOutline)
    realShapes.forEach((shape, idx) => {
      // Encode shape index as a unique RGB color (avoid 0,0,0 = background)
      const r = ((idx + 1) * 37) % 255 + 1
      const g = ((idx + 1) * 73) % 255 + 1
      const b = ((idx + 1) * 119) % 255 + 1
      ctx.save()
      ctx.translate(pad, pad)
      ctx.scale(scale, scale)
      ctx.translate(-minX, -minY)
      // Use thick strokes so parallel stitch lines merge into solid fills
      ctx.lineWidth = 3.0 / scale
      ctx.lineCap = 'round'
      ctx.lineJoin = 'round'
      ctx.strokeStyle = `rgb(${r},${g},${b})`
      shape.segments.forEach(seg => {
        if (seg.type !== 'stitch' || !seg.points || seg.points.length < 2) return
        ctx.beginPath()
        ctx.moveTo(seg.points[0][0], seg.points[0][1])
        for (let i = 1; i < seg.points.length; i++) {
          ctx.lineTo(seg.points[i][0], seg.points[i][1])
        }
        ctx.stroke()
      })
      ctx.restore()
    })

    // Read pixel data — detect edges where a pixel differs from its neighbor
    const imgData = ctx.getImageData(0, 0, cw, ch)
    const px = imgData.data

    // Helper: get pixel "id" (0 = empty, or packed RGB)
    function pixId(x, y) {
      if (x < 0 || x >= cw || y < 0 || y >= ch) return 0
      const i = (y * cw + x) * 4
      if (px[i + 3] < 20) return 0 // transparent = empty
      return (px[i] << 16) | (px[i+1] << 8) | px[i+2]
    }

    // Edge pixel = any non-empty pixel that has a neighbor with a DIFFERENT id
    // (different shape OR empty). This finds outer edges AND inter-shape boundaries.
    const edgeSet = new Set()
    for (let y = 1; y < ch - 1; y++) {
      for (let x = 1; x < cw - 1; x++) {
        const id = pixId(x, y)
        if (id === 0) continue // empty pixel, skip
        let isEdge = false
        for (let dy = -1; dy <= 1 && !isEdge; dy++) {
          for (let dx = -1; dx <= 1 && !isEdge; dx++) {
            if (dx === 0 && dy === 0) continue
            if (pixId(x + dx, y + dy) !== id) isEdge = true
          }
        }
        if (isEdge) edgeSet.add(y * cw + x)
      }
    }
    if (edgeSet.size < 8) return []

    // Find topmost-leftmost start
    let startX = -1, startY = -1
    for (let y = 1; y < ch - 1; y++) {
      for (let x = 1; x < cw - 1; x++) {
        if (edgeSet.has(y * cw + x)) { startX = x; startY = y; break }
      }
      if (startX >= 0) break
    }

    // Moore trace with Jacob's stopping — traces ALL edges (outer + internal)
    const ndx = [1, 1, 0, -1, -1, -1, 0, 1]
    const ndy = [0, 1, 1, 1, 0, -1, -1, -1]
    const contour = [[startX, startY]]
    let cx = startX, cy = startY, dir = 7

    let firstDir = -1
    const scan0 = (dir + 5) % 8
    for (let i = 0; i < 8; i++) {
      const d = (scan0 + i) % 8
      const fnx = cx + ndx[d], fny = cy + ndy[d]
      if (edgeSet.has(fny * cw + fnx)) {
        firstDir = d; cx = fnx; cy = fny; dir = d
        contour.push([cx, cy]); break
      }
    }
    if (firstDir < 0) return []

    for (let step = 0; step < edgeSet.size * 4; step++) {
      const sd = (dir + 5) % 8
      let found = false
      for (let i = 0; i < 8; i++) {
        const d = (sd + i) % 8
        const nnx = cx + ndx[d], nny = cy + ndy[d]
        if (!edgeSet.has(nny * cw + nnx)) continue
        cx = nnx; cy = nny; dir = d
        if (cx === startX && cy === startY && d === firstDir) { found = false; break }
        contour.push([cx, cy]); found = true; break
      }
      if (!found) break
    }
    if (contour.length < 10) return []

    // Convert pixel coords back to world coords
    const cellEquiv = 1 / scale
    const worldContour = contour.map(([bx, by]) => [
      (bx - pad) / scale + minX,
      (by - pad) / scale + minY
    ])

    // Curvature-aware smoothing (5 passes, preserve corners >150°)
    // Multiple passes prevent the per-pixel normal jitter that produces spikes
    // when amplified by the satin perpendicular offset.
    const angleThresh = Math.cos(150 * Math.PI / 180)
    const n = worldContour.length
    const closeDist = Math.hypot(
      worldContour[n-1][0] - worldContour[0][0],
      worldContour[n-1][1] - worldContour[0][1]
    )
    const shouldClose = closeDist < cellEquiv * 3
    let smooth = worldContour.map(p => [p[0], p[1]])
    for (let pass = 0; pass < 5; pass++) {
      const next = []
      for (let i = 0; i < smooth.length; i++) {
        const prev = shouldClose ? smooth[(i - 1 + smooth.length) % smooth.length] : smooth[Math.max(0, i - 1)]
        const curr = smooth[i]
        const nxt = shouldClose ? smooth[(i + 1) % smooth.length] : smooth[Math.min(smooth.length - 1, i + 1)]
        const v1x = prev[0] - curr[0], v1y = prev[1] - curr[1]
        const v2x = nxt[0] - curr[0], v2y = nxt[1] - curr[1]
        const l1 = Math.hypot(v1x, v1y), l2 = Math.hypot(v2x, v2y)
        if (l1 > 0.01 && l2 > 0.01) {
          const dot = (v1x * v2x + v1y * v2y) / (l1 * l2)
          if (dot > angleThresh) { next.push(curr); continue }
        }
        next.push([(prev[0] + curr[0] + nxt[0]) / 3, (prev[1] + curr[1] + nxt[1]) / 3])
      }
      smooth = next
    }

    // Deduplicate
    const deduped = [smooth[0]]
    for (let i = 1; i < smooth.length; i++) {
      if (Math.hypot(smooth[i][0] - deduped[deduped.length-1][0],
                     smooth[i][1] - deduped[deduped.length-1][1]) > cellEquiv * 0.1) {
        deduped.push(smooth[i])
      }
    }
    smooth = deduped
    const cn = smooth.length

    // Compute normals
    const normals = smooth.map((_, i) => {
      const pv = shouldClose ? smooth[(i - 1 + cn) % cn] : smooth[Math.max(0, i - 1)]
      const nv = shouldClose ? smooth[(i + 1) % cn] : smooth[Math.min(cn - 1, i + 1)]
      const tx = nv[0] - pv[0], ty = nv[1] - pv[1]
      const len = Math.hypot(tx, ty)
      if (len < 0.01) return [0, -1]
      return [-ty / len, tx / len]
    })

    // ═══ STITCH TYPE RENDERERS — ON the contour ═══
    const segments = []

    if (stitchType === 'running') {
      const passes = thickness === 'light' ? 1 : thickness === 'medium' ? 2 : 3
      for (let p = 0; p < passes; p++) {
        const pts = smooth.map(pt => [pt[0], pt[1]])
        if (shouldClose) pts.push([...pts[0]])
        segments.push({ type: 'stitch', points: pts })
      }
    } else if (stitchType === 'satin') {
      // Proper satin column: at each step emit a perpendicular zigzag
      // (inner→outer or outer→inner alternating) so consecutive stitches
      // form parallel columns with short along-contour travel between them
      // — not diagonal spikes across the centerline.
      // Half-widths chosen to match real machine-embroidery outline pipings
      // (0.4–1.2mm half = 0.8–2.4mm full satin column). Earlier values of
      // 1/2/3mm half (= 2-6mm full) read as visibly "stupid wide zig zags"
      // on top of designs — pro outlines are narrow piping, not bunting.
      const satinHalf = thickness === 'light' ? 0.4 : thickness === 'medium' ? 0.7 : 1.1
      const stitchSpacing = thickness === 'light' ? 0.4 : thickness === 'medium' ? 0.4 : 0.35
      const points = []
      let distAccum = stitchSpacing
      let goingOut = true
      for (let i = 0; i < cn; i++) {
        if (i > 0) {
          distAccum += Math.hypot(smooth[i][0] - smooth[i-1][0], smooth[i][1] - smooth[i-1][1])
        }
        if (i === 0 || distAccum >= stitchSpacing) {
          const inner = [
            smooth[i][0] - normals[i][0] * satinHalf,
            smooth[i][1] - normals[i][1] * satinHalf
          ]
          const outer = [
            smooth[i][0] + normals[i][0] * satinHalf,
            smooth[i][1] + normals[i][1] * satinHalf
          ]
          if (goingOut) { points.push(inner); points.push(outer) }
          else          { points.push(outer); points.push(inner) }
          goingOut = !goingOut
          distAccum = 0
        }
      }
      if (shouldClose && points.length > 1) points.push([...points[0]])
      segments.push({ type: 'stitch', points })
    } else if (stitchType === 'bean') {
      const beanHalf = thickness === 'light' ? 0.5 : thickness === 'medium' ? 1.0 : 1.5
      for (const offset of [-beanHalf, 0, beanHalf]) {
        const pts = smooth.map((pt, i) => [
          pt[0] + normals[i][0] * offset,
          pt[1] + normals[i][1] * offset
        ])
        if (shouldClose) pts.push([...pts[0]])
        segments.push({ type: 'stitch', points: pts })
      }
    }

    return [{
      id: 'outline-all',
      name: 'Design Outline',
      color: color,
      stitchType: stitchType,
      outlineWidth: 0,
      density: 1.0,
      isOutline: true,
      segments
    }]
  }

  function screenToWorld(sx, sy) {
    return {
      x: (sx - pan.x) / zoom,
      y: (sy - pan.y) / zoom
    }
  }

  function hitTestShape(wx, wy, threshold = 5) {
    const t = threshold / zoom
    for (let i = shapes.length - 1; i >= 0; i--) {
      const shape = shapes[i]
      for (const seg of shape.segments) {
        if (!seg.points) continue
        for (const [px, py] of seg.points) {
          if (Math.abs(px - wx) < t && Math.abs(py - wy) < t) {
            return shape.id
          }
        }
      }
    }
    return null
  }

  function isPointInPolygon(px, py, polygon) {
    let inside = false
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const xi = polygon[i].x, yi = polygon[i].y
      const xj = polygon[j].x, yj = polygon[j].y
      const intersect = ((yi > py) !== (yj > py)) && (px < (xj - xi) * (py - yi) / (yj - yi) + xi)
      if (intersect) inside = !inside
    }
    return inside
  }

  // Graham-scan convex hull on [[x,y], ...] — used by the silhouette
  // overlay so we can quickly fill an approximate shape outline.
  function _convexHull(points) {
    if (points.length < 3) return points.slice()
    const pts = points.map(p => [p[0], p[1]]).sort((a, b) =>
      a[0] === b[0] ? a[1] - b[1] : a[0] - b[0])
    const cross = (o, a, b) =>
      (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0])
    const lower = []
    for (const p of pts) {
      while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) lower.pop()
      lower.push(p)
    }
    const upper = []
    for (let i = pts.length - 1; i >= 0; i--) {
      const p = pts[i]
      while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) upper.pop()
      upper.push(p)
    }
    lower.pop(); upper.pop()
    return lower.concat(upper)
  }

  // ── Undo/Redo (declared before mouse handlers so they can include
  // pushHistory in their useCallback deps without a TDZ error) ──────
  const pushHistory = useCallback((newShapes) => {
    setHistory(prev => [...prev.slice(0, historyIndex + 1), newShapes])
    setHistoryIndex(prev => prev + 1)
  }, [historyIndex])

  const undo = useCallback(() => {
    if (historyIndex > 0) {
      setHistoryIndex(prev => prev - 1)
      setShapes(history[historyIndex - 1])
    }
  }, [history, historyIndex])

  const redo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      setHistoryIndex(prev => prev + 1)
      setShapes(history[historyIndex + 1])
    }
  }, [history, historyIndex])

  // Auto-fit design to currently selected hoop. Scales every stitch point
  // around the design center so the result fits within hoop dimensions
  // with a 5% margin (so the design doesn't kiss the hoop edge).
  // Apply uniform scale around the design center, preserving stitch
  // density. When shrinking (scale < 1), the same point count would
  // become denser per-mm; we drop stitches in PAIRS so satin column
  // L/R alternation stays intact. When enlarging (scale > 1), the same
  // point count becomes sparser per-mm; we leave it (slight under-density
  // is a milder defect than busted satin alternation, and the user can
  // re-run digitization if it matters).
  const applyResize = useCallback((scale) => {
    if (!previewData?.bounds || !shapes || shapes.length === 0) return
    if (!isFinite(scale) || scale <= 0 || Math.abs(scale - 1) < 1e-3) return
    const { minX, minY, maxX, maxY } = previewData.bounds
    const cx = (minX + maxX) / 2, cy = (minY + maxY) / 2
    const next = shapes.map(s => ({
      ...s,
      segments: s.segments.map(seg => {
        const pts = seg.points || []
        if (pts.length === 0) return seg
        const scaled = pts.map(([x, y]) => [
          cx + (x - cx) * scale,
          cy + (y - cy) * scale,
        ])
        if (scale >= 0.97 || pts.length < 4) return { ...seg, points: scaled }
        // Density-preserving downsample: keep `scale` fraction of pairs.
        // Iterate two-at-a-time to preserve L/R alternation in satin.
        const out = []
        let acc = 0
        for (let i = 0; i + 1 < scaled.length; i += 2) {
          acc += scale
          if (acc >= 1.0) {
            out.push(scaled[i])
            out.push(scaled[i + 1])
            acc -= 1.0
          }
        }
        // Always preserve the very last pair so endpoints don't drift.
        if (scaled.length >= 2) {
          const last = scaled[scaled.length - 1]
          const secondLast = scaled[scaled.length - 2]
          if (out.length === 0 ||
              out[out.length - 1][0] !== last[0] ||
              out[out.length - 1][1] !== last[1]) {
            out.push(secondLast, last)
          }
        }
        return { ...seg, points: out }
      }),
    }))
    setShapes(next)
    pushHistory(next)
  }, [previewData, shapes, pushHistory])

  const fitToHoop = useCallback(() => {
    if (!previewData?.bounds) return
    const hoop = HOOP_OPTIONS.find(h => h.id === selectedHoop) || HOOP_OPTIONS[0]
    const { minX, minY, maxX, maxY } = previewData.bounds
    const w = maxX - minX, h = maxY - minY
    if (w <= 0 || h <= 0) return
    const margin = 0.95
    const scale = Math.min((hoop.w * margin) / w, (hoop.h * margin) / h)
    if (scale >= 1) return // already fits
    applyResize(scale)
  }, [previewData, selectedHoop, applyResize])

  // ── Mouse handlers ──────────────────────────────────────
  const getMousePos = useCallback((e) => {
    const rect = canvasRef.current.getBoundingClientRect()
    return { x: e.clientX - rect.left, y: e.clientY - rect.top }
  }, [])

  const handleMouseDown = useCallback((e) => {
    const pos = getMousePos(e)
    setShowBorderMenu(false)
    setShowOutlineMenu(false)

    // Middle mouse or space+click = pan
    if (e.button === 1 || activeTool === 'pan') {
      setIsPanning(true)
      panStart.current = { x: e.clientX - pan.x, y: e.clientY - pan.y }
      return
    }

    if (activeTool === 'select') {
      const world = screenToWorld(pos.x, pos.y)
      // Hit-test jumps first when they're shown — clicking the dashed
      // red line in select mode selects that jump for editing.
      if (showJumps && jumps.length > 0) {
        let bestJ = -1
        let bestDist = 4 / Math.max(0.1, zoom) // 4 screen px tolerance, in world mm
        for (let ji = 0; ji < jumps.length; ji++) {
          const j = jumps[ji]
          // Distance from world point to segment from j.from to j.to
          const ax = j.from[0], ay = j.from[1]
          const bx = j.to[0], by = j.to[1]
          const dx = bx - ax, dy = by - ay
          const len2 = dx * dx + dy * dy
          if (len2 < 1e-6) continue
          let t = ((world.x - ax) * dx + (world.y - ay) * dy) / len2
          t = Math.max(0, Math.min(1, t))
          const px = ax + t * dx, py = ay + t * dy
          const d = Math.hypot(world.x - px, world.y - py)
          if (d < bestDist) { bestDist = d; bestJ = ji }
        }
        if (bestJ >= 0) {
          setSelectedJumpIdx(bestJ)
          setSelectedShapes(new Set())
          setShowProperties(true)
          return
        } else {
          setSelectedJumpIdx(null)
        }
      } else {
        setSelectedJumpIdx(null)
      }
      const hitId = hitTestShape(world.x, world.y)

      if (hitId) {
        // If user clicks an already-selected shape (or any shape while it's
        // already selected with shift), start dragging the whole selection
        // instead of just re-selecting. Plain click on an unselected shape:
        // select it AND start drag from this position.
        let nextSel
        if (e.shiftKey) {
          nextSel = new Set(selectedShapes)
          if (nextSel.has(hitId)) nextSel.delete(hitId)
          else nextSel.add(hitId)
        } else if (selectedShapes.has(hitId)) {
          nextSel = new Set(selectedShapes)
        } else {
          nextSel = new Set([hitId])
        }
        setSelectedShapes(nextSel)
        setShowProperties(true)
        // Begin drag — but only if at least one selection element is real
        // (and not a meta border/outline that auto-regenerates from bounds).
        const draggables = Array.from(nextSel).filter(id => {
          const s = shapes.find(sh => sh.id === id)
          return s && !s.isBorder && !s.isOutline
        })
        if (draggables.length > 0) {
          setIsDraggingShapes(true)
          dragStartWorld.current = { x: world.x, y: world.y }
          dragShapesSnapshot.current = shapes.map(s => ({
            id: s.id,
            segments: s.segments.map(seg => ({
              type: seg.type,
              points: seg.points ? seg.points.map(p => [p[0], p[1]]) : seg.points,
            })),
          }))
        }
      } else {
        // Empty-space click in select mode:
        //   plain   → pan the VIEWPORT (move camera)
        //   shift   → lasso-select shapes inside the dragged region
        //   alt     → drag the whole design (move stitches as one rigid group)
        if (e.shiftKey) {
          setIsLassoing(true)
          setLassoPoints([pos])
          setSelectedShapes(new Set())
        } else if (e.altKey) {
          const draggableIds = shapes
            .filter(s => !s.isBorder && !s.isOutline)
            .map(s => s.id)
          if (draggableIds.length > 0) {
            setSelectedShapes(new Set(draggableIds))
            setIsDraggingShapes(true)
            dragStartWorld.current = { x: world.x, y: world.y }
            dragShapesSnapshot.current = shapes.map(s => ({
              id: s.id,
              segments: s.segments.map(seg => ({
                type: seg.type,
                points: seg.points ? seg.points.map(p => [p[0], p[1]]) : seg.points,
              })),
            }))
          }
        } else {
          setIsPanning(true)
          panStart.current = { x: e.clientX - pan.x, y: e.clientY - pan.y }
          setSelectedShapes(new Set())
        }
      }
    }

    if (activeTool === 'color') {
      const world = screenToWorld(pos.x, pos.y)
      const hitId = hitTestShape(world.x, world.y)
      if (hitId) {
        setSelectedShapes(new Set([hitId]))
        setShowProperties(true)
      }
    }

    if (activeTool === 'text') {
      setShowTextDialog(true)
    }

    if (activeTool === 'erase') {
      // Brush erase — remove only stitch points within brush radius.
      // Big change from previous "click → delete whole shape" behavior
      // which deleted entire words when user tried to fix a single stray
      // stitch. Now you paint over what you want gone, point-by-point.
      const world = screenToWorld(pos.x, pos.y)
      eraseCursorRef.current = world
      setEraseStrokeStarted(true)
      setShapes(prev => {
        const next = eraseInBrushRadius(prev, world, eraseBrushMm)
        return next
      })
      setIsErasing(true)
    }

    if (activeTool === 'satin') {
      // Drop a control point. Double-click handler commits the column.
      const world = screenToWorld(pos.x, pos.y)
      setSatinPoints(prev => [...prev, [world.x, world.y]])
    }
  }, [activeTool, pan, zoom, shapes, getMousePos, pushHistory, eraseBrushMm])

  const handleMouseMove = useCallback((e) => {
    if (isPanning) {
      setPan({
        x: e.clientX - panStart.current.x,
        y: e.clientY - panStart.current.y
      })
      return
    }

    if (isDraggingShapes) {
      const pos = getMousePos(e)
      const world = screenToWorld(pos.x, pos.y)
      const dx = world.x - dragStartWorld.current.x
      const dy = world.y - dragStartWorld.current.y
      const snapshot = dragShapesSnapshot.current
      // Apply delta to selected shapes from the drag-start snapshot so the
      // motion isn't cumulative across move events (which would drift on
      // float-rounding errors). Border / outline shapes auto-regenerate from
      // bounds, skip them — moving them manually breaks the auto-fit.
      setShapes(prev => prev.map(s => {
        if (!selectedShapes.has(s.id)) return s
        if (s.isBorder || s.isOutline) return s
        const snap = snapshot.find(x => x.id === s.id)
        if (!snap) return s
        return {
          ...s,
          segments: s.segments.map((seg, si) => {
            const snapSeg = snap.segments[si]
            if (!snapSeg || !seg.points || !snapSeg.points) return seg
            return {
              ...seg,
              points: snapSeg.points.map(p => [p[0] + dx, p[1] + dy]),
            }
          }),
        }
      }))
      return
    }

    if (isLassoing) {
      const pos = getMousePos(e)
      setLassoPoints(prev => [...(prev || []), pos])
    }

    if (activeTool === 'erase') {
      // Update cursor ring even when not actively dragging, so the user
      // can see the brush size as they hover before clicking.
      const pos = getMousePos(e)
      const world = screenToWorld(pos.x, pos.y)
      eraseCursorRef.current = world
      setEraseCursorScreen(pos)
      if (isErasing) {
        setShapes(prev => eraseInBrushRadius(prev, world, eraseBrushMm))
      }
    } else if (eraseCursorScreen) {
      setEraseCursorScreen(null)
    }
  }, [isPanning, isLassoing, isErasing, activeTool, getMousePos, eraseBrushMm, eraseCursorScreen])

  const handleMouseUp = useCallback(() => {
    setIsPanning(false)
    if (isErasing && eraseStrokeStarted) {
      // Push one history entry per erase STROKE, not per pixel touched.
      pushHistory(shapes)
      setEraseStrokeStarted(false)
    }
    setIsErasing(false)
    if (isDraggingShapes) {
      // Push one history entry per drag — cleaner than per-pixel.
      pushHistory(shapes)
      setIsDraggingShapes(false)
      dragShapesSnapshot.current = []
    }

    if (isLassoing && lassoPoints && lassoPoints.length > 3) {
      // Convert lasso to world coords and find shapes inside
      const worldLasso = lassoPoints.map(p => screenToWorld(p.x, p.y))
      const selected = new Set()

      shapes.forEach(shape => {
        const bbox = getShapeBbox(shape)
        if (!bbox) return
        const cx = bbox.x + bbox.w / 2
        const cy = bbox.y + bbox.h / 2
        if (isPointInPolygon(cx, cy, worldLasso)) {
          selected.add(shape.id)
        }
      })

      if (selected.size > 0) {
        setSelectedShapes(selected)
        setShowProperties(true)
      }
    }

    setIsLassoing(false)
    setLassoPoints(null)
  }, [isLassoing, lassoPoints, shapes, zoom, pan, isDraggingShapes, isErasing,
      eraseStrokeStarted, pushHistory])

  // Zoom with wheel (non-passive to allow preventDefault)
  const handleWheel = useCallback((e) => {
    e.preventDefault()
    const pos = getMousePos(e)
    const zoomFactor = e.deltaY < 0 ? 1.12 : 0.89
    const newZoom = Math.max(0.1, Math.min(50, zoom * zoomFactor))

    // Zoom toward cursor
    const wx = (pos.x - pan.x) / zoom
    const wy = (pos.y - pan.y) / zoom
    setPan({
      x: pos.x - wx * newZoom,
      y: pos.y - wy * newZoom
    })
    setZoom(newZoom)
  }, [zoom, pan, getMousePos])

  // Attach wheel listener with passive: false to allow preventDefault
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    canvas.addEventListener('wheel', handleWheel, { passive: false })
    return () => canvas.removeEventListener('wheel', handleWheel)
  }, [handleWheel])

  // ── Keyboard shortcuts ──────────────────────────────────
  useEffect(() => {
    const handleKey = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return
      if (e.key === 'v' || e.key === 'V') setActiveTool('select')
      if (e.key === 'h' || e.key === 'H') setActiveTool('pan')
      if (e.key === 'e' || e.key === 'E') setActiveTool('erase')
      if (e.key === 't' || e.key === 'T') { setActiveTool('text'); setShowTextDialog(true) }
      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (selectedJumpIdx !== null && jumps[selectedJumpIdx]) {
          // Merge the two segments adjoining the selected jump into a
          // single segment. The merge concatenates points from the
          // source segment, then the target segment — the connecting
          // travel-stitch becomes part of the segment so the machine
          // sews straight through instead of trim+jump+lock.
          const j = jumps[selectedJumpIdx]
          setShapes(prev => {
            const next = prev.map(s => ({ ...s, segments: s.segments.map(seg => ({ ...seg })) }))
            const fromShape = next.find(s => s.id === j.fromShapeId)
            const toShape = next.find(s => s.id === j.toShapeId)
            if (!fromShape || !toShape) return prev
            if (fromShape === toShape && j.fromSegIdx + 1 === j.toSegIdx) {
              // Same shape, adjacent segments — merge in place
              const a = fromShape.segments[j.fromSegIdx].points
              const b = fromShape.segments[j.toSegIdx].points
              fromShape.segments[j.fromSegIdx] = {
                ...fromShape.segments[j.fromSegIdx],
                points: [...a, ...b],
              }
              fromShape.segments.splice(j.toSegIdx, 1)
            } else {
              // Cross-shape jump. Append target segment's points onto
              // source segment, then remove that segment from the target
              // shape. If the target shape becomes empty afterward, drop
              // it entirely.
              const a = fromShape.segments[j.fromSegIdx].points
              const b = toShape.segments[j.toSegIdx].points
              fromShape.segments[j.fromSegIdx] = {
                ...fromShape.segments[j.fromSegIdx],
                points: [...a, ...b],
              }
              toShape.segments.splice(j.toSegIdx, 1)
            }
            // Drop empty shapes
            return next.filter(s => s.segments.some(seg => seg.points && seg.points.length > 0))
          })
          setSelectedJumpIdx(null)
          pushHistory(shapes) // captured before mutation; history reflects current state
        } else if (selectedShapes.size > 0) {
          setShapes(prev => prev.filter(s => !selectedShapes.has(s.id)))
          setSelectedShapes(new Set())
          setShowProperties(false)
        }
      }
      if (e.key === 'Escape') {
        setSelectedShapes(new Set())
        setSelectedJumpIdx(null)
        setShowProperties(false)
        setShowTextDialog(false)
        setShowBorderMenu(false)
        setShowOutlineMenu(false)
      }
      if ((e.metaKey || e.ctrlKey) && e.key === 'z') {
        e.preventDefault()
        undo()
      }
      if ((e.metaKey || e.ctrlKey) && (e.key === 'y' || (e.shiftKey && e.key === 'z'))) {
        e.preventDefault()
        redo()
      }
      if (e.key === '+' || e.key === '=') setZoom(z => Math.min(50, z * 1.2))
      if (e.key === '-') setZoom(z => Math.max(0.1, z / 1.2))
      if (e.key === '0') {
        // Fit view
        if (previewData?.bounds && containerRef.current) {
          const { minX, minY, maxX, maxY } = previewData.bounds
          const rect = containerRef.current.getBoundingClientRect()
          const pad = 60
          const fitZoom = Math.min((rect.width - pad * 2) / (maxX - minX), (rect.height - pad * 2) / (maxY - minY), 8)
          setZoom(fitZoom)
          setPan({
            x: (rect.width / 2) - ((minX + (maxX - minX) / 2) * fitZoom),
            y: (rect.height / 2) - ((minY + (maxY - minY) / 2) * fitZoom)
          })
        }
      }
      // Stitch player keyboard shortcuts (only when player is relevant —
      // i.e. there are stitches to play through).
      if (totalStitchPoints > 0) {
        if (e.key === ' ' || e.code === 'Space') {
          e.preventDefault()
          setIsPlaying(p => {
            // Auto-rewind/auto-rewind-end when starting fresh
            if (!p) {
              if (!reverse && playProgress >= 1) setPlayProgress(0)
              if (reverse && playProgress <= 0) setPlayProgress(1)
            }
            return !p
          })
        }
        if (e.key === 'ArrowLeft') {
          e.preventDefault()
          setIsPlaying(false)
          const mult = e.shiftKey ? 100 : 1
          setPlayProgress(p => Math.max(0, p - mult / Math.max(1, totalStitchPoints)))
        }
        if (e.key === 'ArrowRight') {
          e.preventDefault()
          setIsPlaying(false)
          const mult = e.shiftKey ? 100 : 1
          setPlayProgress(p => Math.min(1, p + mult / Math.max(1, totalStitchPoints)))
        }
        if (e.key === 'Home') { e.preventDefault(); setIsPlaying(false); setPlayProgress(0) }
        if (e.key === 'End')  { e.preventDefault(); setIsPlaying(false); setPlayProgress(1) }
      }
    }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [selectedShapes, previewData, totalStitchPoints, reverse, playProgress])

  // ── Shape editing actions ───────────────────────────────
  const changeShapeColor = useCallback((shapeId, newColor) => {
    setShapes(prev => {
      const next = prev.map(s => s.id === shapeId ? { ...s, color: newColor } : s)
      pushHistory(next)
      return next
    })
  }, [pushHistory])

  const changeSelectedColor = useCallback((newColor) => {
    setShapes(prev => {
      const next = prev.map(s => selectedShapes.has(s.id) ? { ...s, color: newColor } : s)
      pushHistory(next)
      return next
    })
  }, [selectedShapes, pushHistory])

  const deleteSelected = useCallback(() => {
    if (selectedShapes.size === 0) return
    setShapes(prev => {
      const next = prev.filter(s => !selectedShapes.has(s.id))
      pushHistory(next)
      return next
    })
    setSelectedShapes(new Set())
    setShowProperties(false)
  }, [selectedShapes, pushHistory])

  // ── Export ──────────────────────────────────────────────
  // Re-emit PES from current shapes state so user edits (border, outline,
  // recolors, deletes) actually persist into the downloaded file. The
  // original pesBase64 is a fallback if the backend roundtrip fails.
  const [downloading, setDownloading] = useState(false)
  const handleDownloadPES = useCallback(async () => {
    if (downloading) return
    const triggerDownload = (b64) => {
      const blob = new Blob(
        [Uint8Array.from(atob(b64), c => c.charCodeAt(0))],
        { type: 'application/octet-stream' }
      )
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename || 'zenna-embroidery.pes'
      a.click()
      URL.revokeObjectURL(url)
    }
    if (!shapes || shapes.length === 0) {
      if (pesBase64) triggerDownload(pesBase64)
      return
    }
    setDownloading(true)
    try {
      const resp = await fetch(`${API_URL}/export/shapes-to-pes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          shapes: shapes.map(s => ({
            color: s.color,
            colorIndex: s.colorIndex,
            segments: (s.segments || []).map(seg => ({
              type: seg.type || 'stitch',
              points: seg.points || [],
            })),
          })),
          colors: previewData?.colors || [],
        }),
      })
      const data = await resp.json()
      if (data?.success && data?.file) {
        triggerDownload(data.file)
      } else if (pesBase64) {
        console.warn('shapes-to-pes failed, falling back to original PES:', data?.error)
        triggerDownload(pesBase64)
      } else {
        alert('PES export failed: ' + (data?.error || 'unknown error'))
      }
    } catch (e) {
      console.error('PES export request failed:', e)
      if (pesBase64) triggerDownload(pesBase64)
    } finally {
      setDownloading(false)
    }
  }, [shapes, previewData, pesBase64, filename, downloading])

  // ── Color blocks (current sewing order) ─────────────────
  // Walks the shapes list, groups consecutive same-color shapes into
  // blocks so the user can see + reorder the actual sew sequence. Blocks
  // are listed in the order they'll be stitched. Reorder = drag a block
  // up/down → all shapes in that block shift to the new position; other
  // blocks stay where they are.
  const colorBlocks = useMemo(() => {
    const blocks = []
    let cur = null
    shapes.forEach((s, idx) => {
      if (s.isBorder || s.isOutline) return
      let color = (s.color || '#000000').toLowerCase()
      if (previewData?.colors?.length > 0 && s.colorIndex !== undefined) {
        const c = previewData.colors[s.colorIndex]
        if (c) color = (typeof c === 'string' ? c : c.hex || '').toLowerCase()
      }
      if (!cur || cur.color !== color) {
        cur = { color, shapeIds: [], stitches: 0 }
        blocks.push(cur)
      }
      cur.shapeIds.push(s.id)
      for (const seg of s.segments || []) cur.stitches += (seg.points || []).length
    })
    return blocks
  }, [shapes, previewData])

  // Move color block at index `from` to index `to`. Rebuilds shapes by
  // concatenating shapes-by-block in the new order. Border/outline meta
  // shapes stay at their original position (always last so they sew on
  // top of the design).
  const reorderColorBlock = useCallback((from, to) => {
    if (from === to || from < 0 || to < 0
        || from >= colorBlocks.length || to >= colorBlocks.length) return
    const newOrder = [...colorBlocks]
    const [moved] = newOrder.splice(from, 1)
    newOrder.splice(to, 0, moved)
    // Build new shapes array in the new block order. Preserve internal
    // order of shapes within each block. Borders/outlines pinned to end.
    const shapeById = new Map(shapes.map(s => [s.id, s]))
    const next = []
    for (const block of newOrder) {
      for (const id of block.shapeIds) {
        const s = shapeById.get(id)
        if (s) next.push(s)
      }
    }
    // Append meta shapes (border/outline) at the end so they sew last.
    for (const s of shapes) {
      if (s.isBorder || s.isOutline) next.push(s)
    }
    setShapes(next)
    pushHistory(next)
  }, [colorBlocks, shapes, pushHistory])

  // ── Print worksheet (saves as PDF via browser print dialog) ─
  // Generates a printable HTML page with: design preview snapshot,
  // hoop diagram, per-color thread breakdown (brand+code+length), and
  // total stats. Customer-approval workflow piece — replaces having to
  // hand-build a sheet in Word.
  const handlePrintWorksheet = useCallback(async () => {
    try {
      // Snapshot the canvas as a PNG
      const designImg = canvasRef.current?.toDataURL('image/png')
      // Build per-color breakdown from current shapes
      const colorBuckets = new Map()
      for (const s of shapes) {
        if (s.isBorder || s.isOutline) continue
        let color = (s.color || '#000000').toLowerCase()
        if (previewData?.colors?.length > 0 && s.colorIndex !== undefined) {
          const c = previewData.colors[s.colorIndex]
          if (c) color = (typeof c === 'string' ? c : c.hex || '').toLowerCase()
        }
        const bucket = colorBuckets.get(color) || { color, stitches: 0, length_mm: 0 }
        for (const seg of s.segments || []) {
          const pts = seg.points || []
          bucket.stitches += pts.length
          for (let i = 1; i < pts.length; i++) {
            bucket.length_mm += Math.hypot(pts[i][0] - pts[i-1][0], pts[i][1] - pts[i-1][1])
          }
        }
        colorBuckets.set(color, bucket)
      }
      const buckets = Array.from(colorBuckets.values())
      // Match colors to threads (brand picker — default brother)
      let matches = []
      try {
        const r = await fetch(`${API_URL}/export/match-threads`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            hexes: buckets.map(b => b.color),
            brand: 'brother',
          }),
        })
        if (r.ok) {
          const j = await r.json()
          matches = j.matches || []
        }
      } catch {}
      const hoop = HOOP_OPTIONS.find(h => h.id === selectedHoop) || HOOP_OPTIONS[0]
      const fitsHoop = stats?.fitsHoop
      const date = new Date().toLocaleString()
      // Build standalone HTML and pop a new window. User uses browser
      // "Save as PDF" from print dialog — no jsPDF dep needed.
      const html = `<!doctype html>
<html><head><meta charset="utf-8">
<title>Zenna Worksheet — ${filename || 'embroidery'}</title>
<style>
  body { font-family: ui-sans-serif, system-ui, -apple-system, sans-serif; color: #1f2937; padding: 24px; max-width: 800px; margin: 0 auto; }
  h1 { font-size: 22px; margin: 0 0 4px; }
  .meta { color: #6b7280; font-size: 12px; margin-bottom: 18px; }
  .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 18px; margin-bottom: 20px; }
  .card { border: 1px solid #e5e7eb; border-radius: 8px; padding: 12px; }
  .card h2 { font-size: 12px; text-transform: uppercase; letter-spacing: 0.05em; color: #6b7280; margin: 0 0 8px; }
  table { width: 100%; border-collapse: collapse; font-size: 12px; }
  table th, table td { text-align: left; padding: 6px 8px; border-bottom: 1px solid #e5e7eb; }
  table th { background: #f9fafb; font-weight: 600; }
  .swatch { display: inline-block; width: 14px; height: 14px; border-radius: 3px; border: 1px solid #d1d5db; vertical-align: middle; margin-right: 6px; }
  .preview { border: 1px solid #e5e7eb; border-radius: 8px; padding: 8px; text-align: center; }
  .preview img { max-width: 100%; max-height: 300px; }
  .stat { display: flex; justify-content: space-between; padding: 4px 0; font-size: 12px; }
  .stat span:first-child { color: #6b7280; }
  .stat span:last-child { font-weight: 600; }
  .footer { margin-top: 20px; font-size: 10px; color: #9ca3af; text-align: center; }
  @media print { body { padding: 12px; } .no-print { display: none; } }
</style>
</head><body>
<h1>${filename ? filename.replace(/\.pes$/, '') : 'Embroidery Design'}</h1>
<div class="meta">Generated ${date} • Zenna by ZenFlow Studios</div>

<div class="grid">
  <div class="preview">
    ${designImg ? `<img src="${designImg}" alt="Design preview" />` : '<em>preview unavailable</em>'}
  </div>
  <div class="card">
    <h2>Stats</h2>
    <div class="stat"><span>Stitches</span><span>${stats?.stitchCount?.toLocaleString() || '—'}</span></div>
    <div class="stat"><span>Trims</span><span>${stats?.trimCount || 0}</span></div>
    <div class="stat"><span>Jumps</span><span>${stats?.jumpCount || 0}</span></div>
    <div class="stat"><span>Color changes</span><span>${stats?.colorChanges || 0}</span></div>
    <div class="stat"><span>Estimated time</span><span>${stats?.timeFormatted || '—'}</span></div>
    <div class="stat"><span>Top thread</span><span>${stats?.threadLengthM ? stats.threadLengthM + 'm' : '—'}</span></div>
    <div class="stat"><span>Bobbin</span><span>${stats?.bobbinLengthM ? stats.bobbinLengthM + 'm' : '—'}</span></div>
    <div class="stat"><span>Design size</span><span>${stats?.widthMm?.toFixed(0) || '—'}×${stats?.heightMm?.toFixed(0) || '—'}mm</span></div>
    <div class="stat"><span>Hoop</span><span>${hoop.name} ${fitsHoop ? '✓' : '✗ overflows'}</span></div>
    <div class="stat"><span>Density</span><span>${stats?.density?.toFixed(1) || '—'} st/mm²</span></div>
  </div>
</div>

<div class="card">
  <h2>Threads (${buckets.length})</h2>
  <table>
    <thead><tr><th>Color</th><th>Brand #</th><th>Name</th><th>Stitches</th><th>Length (m)</th></tr></thead>
    <tbody>
      ${buckets.map((b, i) => {
        const m = matches[i] || {}
        return `<tr>
          <td><span class="swatch" style="background:${b.color}"></span><code style="font-size:10px">${b.color.toUpperCase()}</code></td>
          <td>${m.code ? `Brother #${m.code}` : '—'}</td>
          <td>${m.name || '—'}</td>
          <td>${b.stitches.toLocaleString()}</td>
          <td>${(b.length_mm / 1000).toFixed(1)}</td>
        </tr>`
      }).join('')}
    </tbody>
  </table>
</div>

<div class="footer">
  Generated by Zenna · zennago.com · Your calm in the crafting chaos
</div>

<div class="no-print" style="margin-top:24px;text-align:center">
  <button onclick="window.print()" style="padding:10px 24px;background:#10b981;color:white;font-weight:bold;border:none;border-radius:6px;cursor:pointer;font-size:14px">Save as PDF / Print</button>
</div>
</body></html>`
      const w = window.open('', '_blank')
      if (!w) { alert('Pop-up blocked — please allow pop-ups for this site to view the worksheet.'); return }
      w.document.write(html)
      w.document.close()
    } catch (e) {
      console.error('worksheet generation failed:', e)
      alert('Worksheet generation failed: ' + (e.message || 'unknown error'))
    }
  }, [shapes, previewData, stats, selectedHoop, filename])

  // ── Selected shape info ─────────────────────────────────
  const selectedInfo = useMemo(() => {
    if (selectedShapes.size === 0) return null
    const selected = shapes.filter(s => selectedShapes.has(s.id))
    const totalStitches = selected.reduce((sum, s) =>
      sum + s.segments.reduce((ss, seg) => ss + (seg.points?.length || 0), 0), 0)
    return {
      count: selected.length,
      totalStitches,
      color: selected.length === 1 ? selected[0].color : null,
      stitchType: selected.length === 1 ? selected[0].stitchType : null,
      name: selected.length === 1 ? selected[0].name : `${selected.length} shapes`,
    }
  }, [selectedShapes, shapes])

  // ════════════════════════════════════════════════════════
  //  RENDER
  // ════════════════════════════════════════════════════════
  return (
    <div className="flex flex-col w-full h-full bg-slate-100 dark:bg-slate-900 overflow-hidden" ref={containerRef}>

      {/* ─── TOOLBAR ─────────────────────────────────────── */}
      <div className="flex items-center gap-1 px-3 py-1.5 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 flex-shrink-0">
        {/* Back button */}
        <button
          onClick={onClose}
          className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 transition-colors mr-2"
        >
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
          Back to Canvas
        </button>

        <div className="w-px h-5 bg-slate-200 dark:bg-slate-700 mx-1" />

        {/* Tool buttons */}
        {Object.values(TOOLS).map(tool => (
          <button
            key={tool.id}
            onClick={() => {
              setActiveTool(tool.id)
              // Text tool opens its dialog immediately so the user can type
              // — old behavior required a separate canvas click first, which
              // looked like "the button does nothing".
              if (tool.id === 'text') setShowTextDialog(true)
            }}
            title={`${tool.label} — ${tool.tip}`}
            className={`px-2.5 py-1.5 text-xs font-bold rounded-lg transition-all ${
              activeTool === tool.id
                ? 'bg-blue-500 text-white shadow-sm shadow-blue-500/30'
                : 'text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-700'
            }`}
          >
            {tool.icon}
          </button>
        ))}

        {/* Erase brush size selector — labeled with actual mm so the user
            can pick a size with intent instead of guessing what
            "small/medium/large" means visually. */}
        {activeTool === 'erase' && (
          <div className="flex items-center gap-1 ml-1 px-1.5 py-0.5 rounded-md bg-slate-100 dark:bg-slate-800">
            <span className="text-[10px] text-slate-500 dark:text-slate-400">Brush:</span>
            {[
              { mm: 0.5,  label: '0.5mm' },
              { mm: 1.0,  label: '1mm' },
              { mm: 2.5,  label: '2.5mm' },
              { mm: 5.0,  label: '5mm' },
              { mm: 10.0, label: '10mm' },
            ].map(b => (
              <button
                key={b.mm}
                onClick={() => setEraseBrushMm(b.mm)}
                className={`text-[10px] px-1.5 py-0.5 rounded ${
                  Math.abs(eraseBrushMm - b.mm) < 0.1
                    ? 'bg-blue-500 text-white'
                    : 'text-slate-500 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700'
                }`}
                title={`Erase brush ${b.label}-radius circle`}
              >
                {b.label}
              </button>
            ))}
            <span className="text-[10px] text-slate-400 dark:text-slate-500 ml-2">
              ⊙ ring on canvas shows footprint
            </span>
          </div>
        )}

        {/* Satin column controls — width selector + cancel + status */}
        {activeTool === 'satin' && (
          <div className="flex items-center gap-1 ml-1 px-1.5 py-0.5 rounded-md bg-slate-100 dark:bg-slate-800">
            <span className="text-[10px] text-slate-500 mr-1">WIDTH</span>
            {[0.8, 1.2, 1.5, 2.0, 3.0].map(w => (
              <button
                key={w}
                onClick={() => setSatinWidthMm(w)}
                className={`text-[10px] px-1.5 py-0.5 rounded ${
                  Math.abs(satinWidthMm - w) < 0.05
                    ? 'bg-blue-500 text-white'
                    : 'text-slate-500 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700'
                }`}
              >
                {w}mm
              </button>
            ))}
            <span className="text-[10px] text-slate-500 ml-2">
              {satinPoints.length === 0
                ? 'click to drop points • dbl-click to finish'
                : `${satinPoints.length} pts • dbl-click to finish`}
            </span>
            {satinPoints.length > 0 && (
              <button
                onClick={() => setSatinPoints([])}
                className="text-[10px] px-1.5 py-0.5 rounded text-rose-500 hover:bg-rose-500/10"
              >
                cancel
              </button>
            )}
          </div>
        )}

        <div className="w-px h-5 bg-slate-200 dark:bg-slate-700 mx-1" />

        {/* Toggle buttons */}
        {[
          { key: 'jumps', label: 'Jumps', state: showJumps, set: setShowJumps, color: 'red' },
          { key: 'trims', label: 'Trims', state: showTrims, set: setShowTrims, color: 'amber' },
          { key: 'grid',  label: 'Grid',  state: showGrid,  set: setShowGrid,  color: 'blue' },
          { key: 'hoop',  label: 'Hoop',  state: showHoop,  set: setShowHoop,  color: 'emerald' },
          { key: 'silhouette', label: 'Silhouette',
            state: showSilhouette, set: setShowSilhouette, color: 'fuchsia' },
        ].map(toggle => (
          <button
            key={toggle.key}
            onClick={() => toggle.set(p => !p)}
            className={`px-2 py-1 text-[10px] font-semibold rounded-md transition-all ${
              toggle.state
                ? `bg-${toggle.color}-100 text-${toggle.color}-700 dark:bg-${toggle.color}-900/40 dark:text-${toggle.color}-300`
                : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300'
            }`}
          >
            {toggle.label}
          </button>
        ))}

        <div className="w-px h-5 bg-slate-200 dark:bg-slate-700 mx-1" />

        {/* Color sort panel — drag to reorder sewing sequence */}
        <div className="relative">
          <button
            onClick={() => setShowColorSort(p => !p)}
            className={`px-2 py-1 text-[10px] font-semibold rounded-md transition-all ${
              showColorSort
                ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300'
                : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300'
            }`}
            title="Drag to reorder which color stitches first"
          >
            Sequence ({colorBlocks.length})
          </button>
          {showColorSort && (
            <div className="absolute top-full left-0 mt-1 bg-slate-900/95 backdrop-blur-md rounded-lg shadow-2xl border border-slate-700/50 z-50 min-w-[260px] py-2 max-h-[400px] overflow-y-auto">
              <div className="px-3 pb-2 text-[9px] font-semibold text-slate-500 uppercase tracking-wider">
                Sew order — drag rows to reorder
              </div>
              {colorBlocks.length === 0 ? (
                <div className="px-3 py-2 text-[10px] text-slate-500">No color blocks yet.</div>
              ) : (
                colorBlocks.map((b, i) => (
                  <div
                    key={`${b.color}-${i}`}
                    draggable
                    onDragStart={() => { colorDragSrcRef.current = i }}
                    onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = 'move' }}
                    onDrop={e => {
                      e.preventDefault()
                      const src = colorDragSrcRef.current
                      colorDragSrcRef.current = null
                      if (src !== null && src !== i) reorderColorBlock(src, i)
                    }}
                    className="flex items-center gap-2 px-3 py-1.5 text-[11px] text-slate-200 hover:bg-slate-800 cursor-grab active:cursor-grabbing border-b border-slate-800 last:border-b-0"
                  >
                    <span className="text-slate-600 select-none">⋮⋮</span>
                    <span className="text-slate-500 w-5 text-center">{i + 1}</span>
                    <span className="inline-block w-4 h-4 rounded-sm border border-slate-600" style={{ backgroundColor: b.color }} />
                    <code className="text-[10px] text-slate-400 flex-1 truncate">{b.color.toUpperCase()}</code>
                    <span className="text-[9px] text-slate-500">{b.stitches.toLocaleString()} st</span>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        <div className="w-px h-5 bg-slate-200 dark:bg-slate-700 mx-1" />

        {/* Fabric BACKGROUND swatches — sets the preview canvas color
            so the user can see how their design will look on different
            fabric colors. Does NOT change thread / shape colors. */}
        <span className="text-[9px] text-slate-400 uppercase tracking-wider mr-1">
          Fabric:
        </span>
        {[
          { c: '#ffffff', l: 'White fabric (preview only)' },
          { c: '#f5f0e8', l: 'Cream fabric (preview only)' },
          { c: '#1a1a1a', l: 'Black fabric (preview only)' },
          { c: '#2c3e50', l: 'Navy fabric (preview only)' },
          { c: '#c0392b', l: 'Red fabric (preview only)' },
          { c: '#f5deb3', l: 'Wheat fabric (preview only)' },
        ].map(f => (
          <button key={f.c} title={f.l} onClick={() => setFabricColor(f.c)}
            className={`w-4 h-4 rounded-full border transition-all ${fabricColor === f.c ? 'border-blue-400 scale-125 ring-1 ring-blue-400' : 'border-slate-400'}`}
            style={{ backgroundColor: f.c }} />
        ))}

        <div className="w-px h-5 bg-slate-200 dark:bg-slate-700 mx-1" />

        {/* Border dropdown */}
        <div className="relative">
          <button
            onClick={() => { setShowBorderMenu(p => !p); setShowOutlineMenu(false) }}
            className={`px-2 py-1 text-[10px] font-semibold rounded-md transition-all ${
              borderWeight !== 'none'
                ? 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300'
                : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300'
            }`}
          >
            Border {borderWeight !== 'none' ? '▾' : ''}
          </button>
          {showBorderMenu && (
            <div className="absolute top-full left-0 mt-1 bg-slate-900/95 backdrop-blur-md rounded-lg shadow-2xl border border-slate-700/50 z-50 min-w-[180px] py-1">
              {BORDER_OPTIONS.map(opt => (
                <button
                  key={opt.id}
                  onClick={() => {
                    setBorderWeight(opt.id)
                    setShowBorderMenu(false)
                  }}
                  className={`w-full text-left px-3 py-2 text-xs transition-colors flex items-center gap-2 ${
                    borderWeight === opt.id
                      ? 'bg-violet-500/20 text-violet-300'
                      : 'text-slate-300 hover:bg-slate-800'
                  }`}
                >
                  {opt.id !== 'none' && (
                    <span className="inline-block rounded-sm border border-current" style={{
                      width: opt.id === 'light' ? 8 : opt.id === 'medium' ? 10 : 13,
                      height: opt.id === 'light' ? 8 : opt.id === 'medium' ? 10 : 13,
                      borderWidth: opt.id === 'light' ? 1 : opt.id === 'medium' ? 2 : 3,
                    }} />
                  )}
                  {opt.id === 'none' && <span className="text-slate-500">--</span>}
                  <span>{opt.name}</span>
                  {borderWeight === opt.id && <span className="ml-auto text-violet-400">✓</span>}
                </button>
              ))}
              {borderWeight !== 'none' && (
                <>
                  <div className="border-t border-slate-700/50 my-1" />
                  <div className="px-3 py-1.5">
                    <label className="text-[9px] font-semibold text-slate-500 uppercase tracking-wider">Shape</label>
                    <div className="grid grid-cols-2 gap-1 mt-1">
                      {BORDER_SHAPES.map(s => (
                        <button
                          key={s.id}
                          onClick={() => setBorderShape(s.id)}
                          className={`text-[10px] py-1 px-1.5 rounded border transition-colors ${
                            borderShape === s.id
                              ? 'border-violet-400 bg-violet-500/20 text-violet-300'
                              : 'border-slate-700 text-slate-400 hover:border-slate-500'
                          }`}
                        >
                          {s.name}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="px-3 py-1.5">
                    <label className="text-[9px] font-semibold text-slate-500 uppercase tracking-wider">Border Color</label>
                    <div className="grid grid-cols-10 gap-0.5 mt-1">
                      {THREAD_PALETTE.slice(0, 20).map(c => (
                        <button
                          key={c}
                          onClick={() => setBorderColor(c)}
                          className={`w-4 h-4 rounded-sm border transition-transform hover:scale-125 ${
                            borderColor === c ? 'border-violet-400 ring-1 ring-violet-400 scale-110' : 'border-slate-600'
                          }`}
                          style={{ backgroundColor: c }}
                        />
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Outline dropdown */}
        <div className="relative">
          <button
            onClick={() => { setShowOutlineMenu(p => !p); setShowBorderMenu(false) }}
            className={`px-2 py-1 text-[10px] font-semibold rounded-md transition-all ${
              outlineType !== 'none'
                ? 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-300'
                : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300'
            }`}
          >
            Outline {outlineType !== 'none' ? '▾' : ''}
          </button>
          {showOutlineMenu && (
            <div className="absolute top-full left-0 mt-1 bg-slate-900/95 backdrop-blur-md rounded-lg shadow-2xl border border-slate-700/50 z-50 min-w-[200px] py-1">
              <div className="px-3 py-1">
                <label className="text-[9px] font-semibold text-slate-500 uppercase tracking-wider">Stitch Type</label>
              </div>
              {OUTLINE_TYPES.map(opt => (
                <button
                  key={opt.id}
                  onClick={() => {
                    setOutlineType(opt.id)
                    if (opt.id === 'none') setShowOutlineMenu(false)
                  }}
                  className={`w-full text-left px-3 py-1.5 text-xs transition-colors flex items-center gap-2 ${
                    outlineType === opt.id
                      ? 'bg-cyan-500/20 text-cyan-300'
                      : 'text-slate-300 hover:bg-slate-800'
                  }`}
                >
                  {opt.id === 'none' && <span className="text-slate-500">--</span>}
                  {opt.id === 'running' && <span className="text-[10px]">───</span>}
                  {opt.id === 'satin' && <span className="text-[10px]">╬╬╬</span>}
                  {opt.id === 'bean' && <span className="text-[10px]">━━━</span>}
                  <span>{opt.name}</span>
                  {opt.desc && <span className="text-[9px] text-slate-500 ml-auto">{opt.desc}</span>}
                  {outlineType === opt.id && <span className="ml-auto text-cyan-400">✓</span>}
                </button>
              ))}
              {outlineType !== 'none' && (
                <>
                  <div className="border-t border-slate-700/50 my-1" />
                  <div className="px-3 py-1">
                    <label className="text-[9px] font-semibold text-slate-500 uppercase tracking-wider">Thickness</label>
                  </div>
                  <div className="flex gap-1 px-3 pb-1.5">
                    {OUTLINE_THICKNESS.map(t => (
                      <button
                        key={t.id}
                        onClick={() => setOutlineThickness(t.id)}
                        className={`flex-1 text-center py-1 text-[10px] rounded transition-colors ${
                          outlineThickness === t.id
                            ? 'bg-cyan-500/30 text-cyan-300 border border-cyan-500/50'
                            : 'text-slate-400 hover:bg-slate-800 border border-slate-700/50'
                        }`}
                      >
                        {t.name}
                      </button>
                    ))}
                  </div>
                  <div className="border-t border-slate-700/50 my-1" />
                  <div className="px-3 py-1.5">
                    <label className="text-[9px] font-semibold text-slate-500 uppercase tracking-wider">Outline Color</label>
                    <div className="grid grid-cols-10 gap-0.5 mt-1">
                      {THREAD_PALETTE.slice(0, 20).map(c => (
                        <button
                          key={c}
                          onClick={() => setOutlineColor(c)}
                          className={`w-4 h-4 rounded-sm border transition-transform hover:scale-125 ${
                            outlineColor === c ? 'border-cyan-400 ring-1 ring-cyan-400 scale-110' : 'border-slate-600'
                          }`}
                          style={{ backgroundColor: c }}
                        />
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        <div className="flex-1" />

        {/* Zoom controls */}
        <span className="text-[10px] text-slate-400 mr-1">{Math.round(zoom * 100)}%</span>
        <button onClick={() => setZoom(z => Math.max(0.1, z / 1.3))}
          className="px-1.5 py-0.5 text-xs text-slate-400 hover:text-slate-600 dark:hover:text-slate-300">−</button>
        <button onClick={() => setZoom(z => Math.min(50, z * 1.3))}
          className="px-1.5 py-0.5 text-xs text-slate-400 hover:text-slate-600 dark:hover:text-slate-300">+</button>
        <button onClick={() => { /* fit view */ }}
          className="px-1.5 py-0.5 text-[10px] text-slate-400 hover:text-slate-600 dark:hover:text-slate-300">Fit</button>

        <div className="w-px h-5 bg-slate-200 dark:bg-slate-700 mx-1" />

        {/* Undo/Redo */}
        <button onClick={undo} disabled={historyIndex <= 0}
          className="px-1.5 py-1 text-xs text-slate-400 hover:text-slate-600 disabled:opacity-30">↩</button>
        <button onClick={redo} disabled={historyIndex >= history.length - 1}
          className="px-1.5 py-1 text-xs text-slate-400 hover:text-slate-600 disabled:opacity-30">↪</button>

        <div className="w-px h-5 bg-slate-200 dark:bg-slate-700 mx-1" />

        {/* Print worksheet — opens a printable HTML page in a new tab */}
        <button
          onClick={handlePrintWorksheet}
          className="flex items-center gap-1.5 px-2.5 py-1.5 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 text-xs font-bold rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
          title="Generate a printable worksheet (per-color thread breakdown, stats, design preview). Save as PDF from the print dialog."
        >
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
          </svg>
          Worksheet
        </button>

        {/* Export */}
        <button
          onClick={handleDownloadPES}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-bold rounded-lg transition-colors shadow-sm shadow-emerald-500/30"
        >
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
          Download PES
        </button>
      </div>

      {/* ─── CANVAS ──────────────────────────────────────── */}
      <div className="flex-1 relative overflow-hidden">
        <canvas
          ref={canvasRef}
          className="absolute inset-0"
          style={{ cursor: activeTool === 'erase' ? 'none' : (TOOLS[activeTool]?.cursor || 'default') }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onDoubleClick={() => {
            // Commit the in-progress satin column. Need 2+ control
            // points; we drop the LAST one because dblclick adds an
            // extra duplicate point on top of the previous click.
            if (activeTool !== 'satin' || satinPoints.length < 2) return
            const cl = satinPoints.slice(0, -1).length >= 2
              ? satinPoints.slice(0, -1)
              : satinPoints
            const points = buildSatinColumnPoints(cl, satinWidthMm)
            if (points.length < 2) {
              setSatinPoints([])
              return
            }
            const newShape = {
              id: `satin-${Date.now()}`,
              name: `Satin column (${satinWidthMm}mm)`,
              color: borderColor,
              stitchType: 'satin',
              isManualSatin: true,
              segments: [{ type: 'stitch', points }],
            }
            setShapes(prev => {
              const next = [...prev, newShape]
              pushHistory(next)
              return next
            })
            setSatinPoints([])
          }}
          onContextMenu={e => e.preventDefault()}
        />

        {/* Erase brush ring overlay — visible only while erase tool is active.
            Sized in screen pixels (radius * zoom * 2) so it accurately
            represents the brush footprint on the design. */}
        {activeTool === 'erase' && eraseCursorScreen && (
          <div
            className="absolute pointer-events-none rounded-full border-2"
            style={{
              left: eraseCursorScreen.x - eraseBrushMm * zoom,
              top: eraseCursorScreen.y - eraseBrushMm * zoom,
              width:  eraseBrushMm * zoom * 2,
              height: eraseBrushMm * zoom * 2,
              borderColor: isErasing ? '#ef4444' : '#94a3b8',
              backgroundColor: isErasing ? 'rgba(239,68,68,0.10)' : 'transparent',
              transition: 'border-color 80ms ease',
              zIndex: 15,
            }}
          />
        )}

        {/* ── STITCH PLAYER ─────────────────────────────── */}
        <StitchPlayer
          shapes={shapes}
          previewData={previewData}
          totalStitchPoints={totalStitchPoints}
          playProgress={playProgress}
          setPlayProgress={setPlayProgress}
          isPlaying={isPlaying}
          setIsPlaying={setIsPlaying}
          playSpeed={playSpeed}
          setPlaySpeed={setPlaySpeed}
          pauseOnColorChange={pauseOnColorChange}
          setPauseOnColorChange={setPauseOnColorChange}
          autoScroll={autoScroll}
          setAutoScroll={setAutoScroll}
          reverse={reverse}
          setReverse={setReverse}
          canvasRef={canvasRef}
          filename={filename}
        />

        {/* ── FLOATING PROPERTIES PANEL ─────────────────── */}
        <FloatingPanel
          title={selectedInfo ? selectedInfo.name : 'Properties'}
          isOpen={showProperties && selectedInfo !== null}
          onClose={() => setShowProperties(false)}
          defaultPos={{ x: 20, y: 80 }}
        >
          {selectedInfo && (
            <>
              {/* Shape info */}
              <div className="flex items-center justify-between text-slate-400">
                <span>{selectedInfo.totalStitches} stitches</span>
                <span>{selectedInfo.count} selected</span>
              </div>

              {/* Color */}
              <div>
                <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Thread Color</label>
                <div className="grid grid-cols-10 gap-1 mt-1.5">
                  {THREAD_PALETTE.map(c => (
                    <button
                      key={c}
                      onClick={() => changeSelectedColor(c)}
                      className={`w-5 h-5 rounded-sm border transition-transform hover:scale-125 ${
                        selectedInfo.color === c ? 'border-blue-400 ring-1 ring-blue-400 scale-110' : 'border-slate-600'
                      }`}
                      style={{ backgroundColor: c }}
                      title={c}
                    />
                  ))}
                </div>
              </div>

              {/* Stitch Type */}
              <div>
                <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Stitch Type</label>
                <div className="space-y-1 mt-1.5">
                  {STITCH_TYPES.map(st => (
                    <button
                      key={st.id}
                      onClick={() => {
                        // Future: re-digitize shape with this stitch type
                        setShapes(prev => prev.map(s =>
                          selectedShapes.has(s.id) ? { ...s, stitchType: st.id } : s
                        ))
                      }}
                      className={`w-full text-left px-2.5 py-1.5 rounded-lg transition-colors ${
                        selectedInfo.stitchType === st.id
                          ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30'
                          : 'text-slate-400 hover:bg-slate-800 border border-transparent'
                      }`}
                    >
                      <div className="text-xs font-medium">{st.name}</div>
                      <div className="text-[10px] text-slate-500">{st.desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Outline */}
              <div>
                <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Satin Outline</label>
                <div className="flex items-center gap-2 mt-1.5">
                  <input
                    type="range"
                    min="0" max="2" step="0.1"
                    defaultValue="0"
                    onChange={(e) => {
                      const val = parseFloat(e.target.value)
                      setShapes(prev => prev.map(s =>
                        selectedShapes.has(s.id) ? { ...s, outlineWidth: val } : s
                      ))
                    }}
                    className="flex-1 h-1 accent-blue-500"
                  />
                  <span className="text-[10px] text-slate-500 w-10 text-right">
                    {shapes.find(s => selectedShapes.has(s.id))?.outlineWidth?.toFixed(1) || '0.0'}mm
                  </span>
                </div>
              </div>

              {/* Density */}
              <div>
                <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Fill Density</label>
                <div className="flex items-center gap-2 mt-1.5">
                  <input
                    type="range"
                    min="0.5" max="2" step="0.1"
                    defaultValue="1"
                    onChange={(e) => {
                      setShapes(prev => prev.map(s =>
                        selectedShapes.has(s.id) ? { ...s, density: parseFloat(e.target.value) } : s
                      ))
                    }}
                    className="flex-1 h-1 accent-blue-500"
                  />
                  <span className="text-[10px] text-slate-500 w-10 text-right">
                    {shapes.find(s => selectedShapes.has(s.id))?.density?.toFixed(1) || '1.0'}x
                  </span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-1">
                <button
                  onClick={deleteSelected}
                  className="flex-1 px-2 py-1.5 text-[10px] font-semibold text-red-400 bg-red-500/10 hover:bg-red-500/20 rounded-lg transition-colors"
                >
                  Delete Selected
                </button>
                <button
                  disabled={restitching}
                  onClick={async () => {
                    const selected = shapes.filter(s => selectedShapes.has(s.id))
                    if (selected.length === 0 || restitching) return
                    setRestitching(true)
                    try {
                      const updates = await Promise.all(selected.map(async (sh) => {
                        const pts = (sh.segments || []).flatMap(seg => seg.points || [])
                        if (pts.length < 3) return null
                        const colorStr = (typeof sh.color === 'string')
                          ? sh.color
                          : (previewData?.colors?.[sh.colorIndex]?.hex || '#000000')
                        const resp = await fetch(`${API_URL}/export/restitch-shape`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({
                            points: pts,
                            stitch_type: sh.stitchType || 'fill',
                            density: sh.density || 1.0,
                            color: colorStr,
                          }),
                        })
                        const data = await resp.json()
                        if (!data?.success) throw new Error(data?.error || 'restitch failed')
                        return { id: sh.id, segments: data.segments }
                      }))
                      const upd = new Map(updates.filter(Boolean).map(u => [u.id, u.segments]))
                      setShapes(prev => {
                        const next = prev.map(s =>
                          upd.has(s.id) ? { ...s, segments: upd.get(s.id) } : s)
                        pushHistory(next)
                        return next
                      })
                    } catch (err) {
                      console.error('Re-stitch failed:', err)
                      alert('Re-stitch failed: ' + (err.message || 'unknown error'))
                    } finally {
                      setRestitching(false)
                    }
                  }}
                  className="flex-1 px-2 py-1.5 text-[10px] font-semibold text-blue-400 bg-blue-500/10 hover:bg-blue-500/20 disabled:opacity-40 rounded-lg transition-colors"
                >
                  {restitching ? 'Re-Stitching…' : 'Re-Stitch'}
                </button>
              </div>
            </>
          )}
        </FloatingPanel>

        {/* ── TEXT DIALOG (floating) ────────────────────── */}
        <FloatingPanel
          title="Add Embroidery Text"
          isOpen={showTextDialog}
          onClose={() => setShowTextDialog(false)}
          defaultPos={{ x: 200, y: 100 }}
        >
          <div>
            <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Text</label>
            <input
              type="text"
              value={textInput}
              onChange={e => setTextInput(e.target.value)}
              placeholder="FARM SHARPE"
              className="w-full mt-1 px-2.5 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
              autoFocus
            />
          </div>
          {/* Font picker — visual thumbnail grid (not just a dropdown).
              Each thumbnail shows the font rendered as embroidery so the
              user picks based on actual stitch appearance, not on-screen
              TTF rendering. */}
          <div>
            <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Font</label>
            <div className="grid grid-cols-2 gap-1.5 mt-1">
              {(fontPreviews.length > 0 ? fontPreviews : FONT_OPTIONS).map(f => {
                const isPv = !!f.previewBase64
                const isActive = textFont === (f.key || f.id)
                return (
                  <button
                    key={f.key || f.id}
                    onClick={() => setTextFont(f.key || f.id)}
                    className={`block rounded border-2 overflow-hidden transition-colors ${
                      isActive
                        ? 'border-blue-400'
                        : 'border-slate-700 hover:border-slate-500'
                    }`}
                    title={f.name}
                  >
                    {isPv ? (
                      <img
                        src={`data:image/png;base64,${f.previewBase64}`}
                        alt={f.name}
                        className="w-full h-10 object-cover bg-[#f5f0e8]"
                      />
                    ) : (
                      <div className="w-full h-10 flex items-center justify-center bg-slate-800 text-xs text-slate-300">
                        {f.name}
                      </div>
                    )}
                    <div className={`px-1 py-0.5 text-[9px] truncate ${
                      isActive
                        ? 'bg-blue-500/20 text-blue-300'
                        : 'bg-slate-800 text-slate-400'
                    }`}>{f.name}</div>
                  </button>
                )
              })}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Size (mm)</label>
              <input
                type="number"
                value={textSize}
                onChange={e => setTextSize(Number(e.target.value))}
                min={5} max={100}
                className="w-full mt-1 px-2 py-1.5 bg-slate-800 border border-slate-700 rounded-lg text-white text-xs"
              />
            </div>
            <div>
              <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Baseline</label>
              <select
                value={textBaseline}
                onChange={e => setTextBaseline(e.target.value)}
                className="w-full mt-1 px-2 py-1.5 bg-slate-800 border border-slate-700 rounded-lg text-white text-xs"
              >
                <option value="straight">Straight</option>
                <option value="arc">Arc (smile)</option>
                <option value="reverse_arc">Arc (frown)</option>
                <option value="circle">Circle</option>
              </select>
            </div>
          </div>
          {textBaseline !== 'straight' && (
            <div>
              <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
                Curve radius: {textBaselineRadius}mm
              </label>
              <input
                type="range"
                min={20} max={200} step={5}
                value={textBaselineRadius}
                onChange={e => setTextBaselineRadius(Number(e.target.value))}
                className="w-full mt-1 accent-blue-500"
              />
            </div>
          )}
          <div>
            <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Stitch Type</label>
            <div className="flex gap-1.5 mt-1">
              {STITCH_TYPES.slice(0, 2).map(st => (
                <button
                  key={st.id}
                  onClick={() => setTextStitchType(st.id)}
                  className={`flex-1 px-2 py-1.5 text-[10px] font-semibold rounded-lg transition-colors ${
                    textStitchType === st.id
                      ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30'
                      : 'text-slate-400 bg-slate-800 border border-slate-700 hover:border-slate-600'
                  }`}
                >
                  {st.name}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Color</label>
            <div className="grid grid-cols-10 gap-1 mt-1">
              {THREAD_PALETTE.slice(0, 20).map(c => (
                <button
                  key={c}
                  onClick={() => setTextColor(c)}
                  className={`w-5 h-5 rounded-sm border transition-transform hover:scale-125 ${
                    textColor === c ? 'border-blue-400 ring-1 ring-blue-400 scale-110' : 'border-slate-600'
                  }`}
                  style={{ backgroundColor: c }}
                />
              ))}
            </div>
          </div>
          <button
            onClick={async () => {
              if (!textInput.trim() || addingText) return
              setAddingText(true)
              try {
                const colorHex = (textColor || '#000000').replace('#', '')
                const resp = await fetch(`${API_URL}/export/text-to-stitches`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    text: textInput,
                    font: textFont,
                    size_mm: textSize,
                    stitch_type: textStitchType,
                    color: colorHex,
                    baseline: textBaseline,
                    baseline_radius_mm: textBaselineRadius,
                  }),
                })
                const data = await resp.json()
                if (!data?.success || !Array.isArray(data.shapes) || data.shapes.length === 0) {
                  throw new Error(data?.error || 'Text generation returned no stitches')
                }
                // Center text near the design center; user can drag later when
                // shape repositioning is built. For now, place at design center.
                const b = previewData?.bounds
                const tb = data.bounds || { minX: 0, minY: 0, maxX: 0, maxY: 0 }
                const tw = (tb.maxX - tb.minX) || 0
                const th = (tb.maxY - tb.minY) || 0
                const targetCx = b ? (b.minX + b.maxX) / 2 : tw / 2
                const targetCy = b ? (b.minY + b.maxY) / 2 : th / 2
                const srcCx = (tb.minX + tb.maxX) / 2
                const srcCy = (tb.minY + tb.maxY) / 2
                const dx = targetCx - srcCx
                const dy = targetCy - srcCy
                const newShapes = data.shapes.map((s, i) => ({
                  ...s,
                  id: `text-${Date.now()}-${i}`,
                  name: `Text "${textInput.slice(0, 16)}"`,
                  isText: true,
                  stitchType: textStitchType,
                  segments: (s.segments || []).map(seg => ({
                    ...seg,
                    points: (seg.points || []).map(([x, y]) => [x + dx, y + dy]),
                  })),
                }))
                setShapes(prev => {
                  const next = [...prev, ...newShapes]
                  pushHistory(next)
                  return next
                })
                setShowTextDialog(false)
                setTextInput('')
                // Snap back to SELECT tool so the next canvas click lets
                // the user select / move / adjust the placed text instead
                // of re-opening the text dialog. Was a major UX papercut —
                // every interaction with placed text bounced you back to
                // the "Add text" dialog with no way to modify what's there.
                setActiveTool('select')
              } catch (err) {
                console.error('Add text failed:', err)
                alert('Text generation failed: ' + (err.message || 'unknown error'))
              } finally {
                setAddingText(false)
              }
            }}
            disabled={!textInput.trim() || addingText}
            className="w-full px-3 py-2 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-40 text-white text-xs font-bold rounded-lg transition-colors"
          >
            {addingText ? 'Generating…' : 'Add Text to Design'}
          </button>
        </FloatingPanel>
      </div>

      {/* ─── PRE-STITCH VALIDATION BAR ──────────────────── */}
      {/* Pro-tool style readout: counts + estimated time + bobbin meters */}
      {/* + hoop fit + density + long-jump warnings, all color-coded. */}
      <div className="flex items-center gap-3 px-3 py-1.5 bg-white dark:bg-slate-800 border-t border-slate-200 dark:border-slate-700 text-[10px] flex-shrink-0 flex-wrap">
        <Stat label="Stitches" value={stats?.stitchCount?.toLocaleString()} />
        <Stat label="Trims"    value={stats?.trimCount} />
        <Stat label="Jumps"    value={stats?.jumpCount} />
        <Stat label="Colors"   value={stats?.colorCount} />
        <Sep />
        <Stat label="Time"      value={stats?.timeFormatted} />
        <Stat label="Top thread" value={stats?.threadLengthM ? `${stats.threadLengthM}m` : '—'} />
        <Stat label="Bobbin"    value={stats?.bobbinM ? `${stats.bobbinM}m` : '—'} />
        <Sep />
        <Stat label="Size"   value={stats ? `${stats.widthMm}×${stats.heightMm}mm` : '—'} />
        <Stat
          label="Density"
          value={stats ? `${stats.densityValue} st/mm²` : '—'}
          status={stats?.densityStatus === 'high' ? 'error'
                : stats?.densityStatus === 'low' ? 'warn' : 'ok'}
          tooltip={
            stats?.densityStatus === 'high' ? 'Density too high — risk of puckering and needle breaks. Reduce density or increase row spacing.'
              : stats?.densityStatus === 'low' ? 'Density too low — fabric may show through fill. Increase density.'
              : 'Density in normal range (0.5–50 st/mm²)'
          }
        />
        <Sep />
        {/* Hoop selector + fit indicator */}
        <label className="flex items-center gap-1 text-slate-500">
          Hoop:
          <select
            value={selectedHoop}
            onChange={e => setSelectedHoop(e.target.value)}
            className="bg-transparent border border-slate-300 dark:border-slate-600 rounded px-1 py-0.5 text-[10px] text-slate-700 dark:text-slate-200"
            title={stats?.hoop?.machines || ''}
          >
            {HOOP_OPTIONS.map(h => <option key={h.id} value={h.id}>{h.name}</option>)}
          </select>
        </label>
        <Stat
          label="Fit"
          value={stats?.fitsHoop ? '✓ Fits' : '✗ Too big'}
          status={stats?.fitsHoop ? 'ok' : 'error'}
          tooltip={stats?.fitsHoop
            ? `Design ${stats?.widthMm}×${stats?.heightMm}mm fits ${stats?.hoop?.name} (${stats?.hoop?.w}×${stats?.hoop?.h}mm)`
            : `Design ${stats?.widthMm}×${stats?.heightMm}mm exceeds ${stats?.hoop?.name} (${stats?.hoop?.w}×${stats?.hoop?.h}mm). Click "Fit to hoop" to auto-scale.`
          }
        />
        {!stats?.fitsHoop && (
          <button
            onClick={fitToHoop}
            className="px-2 py-0.5 rounded text-[10px] font-bold bg-amber-500 hover:bg-amber-600 text-white transition-colors"
            title={`Scale design to fit ${stats?.hoop?.name} with a small margin`}
          >
            Fit to hoop
          </button>
        )}
        {/* Resize input — uniform scale around design center, with
            density-preserving downsample on shrink. Lets the user dial
            in an exact mm width without re-running the cleanup pipeline. */}
        <label className="flex items-center gap-1 text-slate-500" title="Resize design (mm width). Density adjusts when shrinking.">
          Resize:
          <input
            type="number"
            min="10"
            max="500"
            step="1"
            defaultValue={Math.round(stats?.widthMm || 100)}
            key={Math.round(stats?.widthMm || 100)}
            className="bg-transparent border border-slate-300 dark:border-slate-600 rounded px-1 py-0.5 text-[10px] w-12 text-slate-700 dark:text-slate-200"
            onKeyDown={e => {
              if (e.key === 'Enter') {
                const target = parseFloat(e.target.value)
                if (isFinite(target) && target > 0 && stats?.widthMm > 0) {
                  applyResize(target / stats.widthMm)
                  e.target.blur()
                }
              }
            }}
            onBlur={e => {
              const target = parseFloat(e.target.value)
              if (isFinite(target) && target > 0 && stats?.widthMm > 0
                  && Math.abs(target - stats.widthMm) > 0.5) {
                applyResize(target / stats.widthMm)
              }
            }}
          />
          <span className="text-[10px] text-slate-400">mm</span>
        </label>
        {stats?.longStitchCount > 0 && (
          <Stat
            label="Long stitches"
            value={`${stats.longStitchCount}`}
            status="warn"
            tooltip={`${stats.longStitchCount} stitches exceed 7mm — risk of snagging on garment use.`}
          />
        )}
        <div className="flex-1" />
        <span className="text-slate-500">
          {activeTool === 'select' ? 'Click to select • Drag to lasso • Shift+click for multi-select' :
           activeTool === 'pan' ? 'Drag to pan • Scroll to zoom' :
           activeTool === 'text' ? 'Click to place text' :
           activeTool === 'erase' ? 'Click or drag to erase' :
           activeTool === 'color' ? 'Click a shape to recolor' :
           'Shift+drag to pan • Scroll to zoom'}
        </span>
      </div>
    </div>
  )
}

function Stat ({ label, value, status, tooltip }) {
  const statusClass =
    status === 'error' ? 'text-red-500'
    : status === 'warn'  ? 'text-amber-500'
    : status === 'ok'    ? 'text-emerald-500'
    : 'text-slate-700 dark:text-slate-200'
  return (
    <span className="text-slate-400" title={tooltip || ''}>
      {label}: <strong className={statusClass}>{value ?? '—'}</strong>
    </span>
  )
}

function Sep () {
  return <span className="text-slate-300 dark:text-slate-700">·</span>
}